/**
 * Shared helpers for the Numa Dashboard.
 *
 * Three jobs:
 *   1. Formats for money, counts and durations.
 *   2. The time window, and the sums of a daily series inside it.
 *   3. The sums that turn a set of client snapshots into one view (a fleet-wide
 *      view is added up in the browser, never stored).
 *
 * Figures for the screen come from selectors.ts, which is built on this file.
 */
import { normalisePartner } from '@/config/contract';
import { snapshotContract } from './contracts';
import type { ContractOf } from './contracts';
import { STALE_AFTER_HOURS } from './fleetData';
import type { FleetTier } from './fleetData';
import type {
  AgentRow,
  AggregateView,
  ByCategoryBlock,
  ByClientRow,
  ChatBlock,
  ClientConfigSummary,
  ClientSnapshot,
  CostExplorerBlock,
  DashboardView,
  ImpactBlock,
  InactiveSpendBlock,
  KnowledgeBaseRow,
  PerAgentStats,
  PerUserStats,
  PortalMeta,
  ScheduledRunsBlock,
  ScheduleRow,
  SchedulesBlock,
  StaleStack,
  TopConversation,
  TopScheduledRun,
  UserAppRow,
  UserAppsBlock,
  UserAppsDailyKey,
  UserAppsInventory,
  UserAppsPerson,
  UserAppsTotals,
} from '@/types/fleetAnalytics';

// ─── formatters ──────────────────────────────────────────────────────────
//
// Currency state is a module-level singleton driven by CurrencyContext. The
// formatters below are pure functions of (n) only: components re-render via
// useCurrency() and the formatters pick up the new state at the next call.
// Source data is always USD. Conversion is for display only.

interface CurrencyState {
  /** Display currency code: 'USD' or any ISO 4217 code. */
  code: string;
  /** Multiplier applied to USD numbers before display. 1.0 for USD. */
  rate: number;
  /** Shown before the number: '$' for USD, 'NZ$' for NZD. */
  prefix: string;
}

// Default NZD, the same as CurrencyProvider's default (DEFAULT_FX_NZD in
// services/fxService.ts, kept as a literal here to avoid a circular import).
// The provider pushes the stored choice and the live rate here on mount.
let _currency: CurrencyState = { code: 'NZD', rate: 1.69, prefix: 'NZ$' };

/** Replace the module-level currency state. Called by CurrencyProvider. */
export function setCurrencyState(next: CurrencyState): void {
  _currency = next;
}

export function getCurrencyState(): CurrencyState {
  return _currency;
}

export const fmtUSD = (n: number | null | undefined) =>
  _currency.prefix +
  ((n ?? 0) * _currency.rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtUSDc = (n: number | null | undefined) =>
  _currency.prefix +
  ((n ?? 0) * _currency.rate).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/** Money with no decimals, for places where space is tight. */
export const fmtUSD0 = (n: number | null | undefined) =>
  _currency.prefix + Math.round((n ?? 0) * _currency.rate).toLocaleString();

export const fmtN = (n: number | null | undefined) => Math.round(n ?? 0).toLocaleString();

/** "12.3%". With no total there is no share to state: "n/a". */
export const pct = (n: number | null | undefined, total: number | null | undefined) =>
  total ? ((100 * (n ?? 0)) / total).toFixed(1) + '%' : 'n/a';

// ─── days ────────────────────────────────────────────────────────────────

export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
export const parseIsoDay = (s: string): Date => new Date(s + 'T00:00:00Z');

/** The day `n` days after `day` (before it when `n` is negative). */
export function addDays(day: string, n: number): string {
  const d = parseIsoDay(day);
  d.setUTCDate(d.getUTCDate() + n);
  return isoDay(d);
}

const todayIso = (): string => isoDay(new Date(Date.now()));

// ─── the time window ─────────────────────────────────────────────────────

export interface WindowState {
  startDate: string; // YYYY-MM-DD, inclusive
  endDate: string; // YYYY-MM-DD, inclusive
  label: string;
}

export function setLastNDays(n: number): WindowState {
  const end = todayIso();
  return { startDate: addDays(end, -(n - 1)), endDate: end, label: `Last ${n} days` };
}

export function setMonth(year: number, month0: number): WindowState {
  const start = new Date(Date.UTC(year, month0, 1));
  const end = new Date(Date.UTC(year, month0 + 1, 0));
  const m = start.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
  const sameYear = year === new Date(Date.now()).getUTCFullYear();
  return {
    startDate: isoDay(start),
    endDate: isoDay(end),
    label: sameYear ? m : `${m} ${year}`,
  };
}

/** A date range picked by hand. */
export function setCustomRange(startIso: string, endIso: string): WindowState {
  const [start, end] = startIso <= endIso ? [startIso, endIso] : [endIso, startIso];
  return { startDate: start, endDate: end, label: `${start} → ${end}` };
}

export const ALL_WINDOW_LABEL = 'All';

/**
 * The "All" window: from the first day this view holds data for, to today.
 * The first day differs from view to view, so the page works this window out
 * again each time the selection changes (see `resolveWindow`). Returns null
 * when the view holds no daily data at all.
 */
export function setAllData(data: DashboardView): WindowState | null {
  let min: string | null = null;
  const consider = (m: Record<string, unknown> | undefined) => {
    for (const d of Object.keys(m || {})) {
      if (!min || d < min) min = d;
    }
  };
  consider(data.cost_explorer?.totals_by_day);
  consider(data.chat?.daily_cost);
  consider(data.chat?.daily_convs);
  if (!min) return null;
  return { startDate: min, endDate: todayIso(), label: ALL_WINDOW_LABEL };
}

/**
 * The window a view is drawn with. Every window but "All" is the one the
 * person picked. "All" starts on this view's own first day: without this the
 * start date of the view that was on screen when "All" was clicked stays, and
 * a client with a longer history loses its early months.
 */
export function resolveWindow(picked: WindowState, data: DashboardView | null): WindowState {
  if (picked.label !== ALL_WINDOW_LABEL || !data) return picked;
  return setAllData(data) ?? picked;
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "14 Sep 2026" from a `YYYY-MM-DD` key. The day is the one the key names, in no time zone. */
export function dayWords(day: string, withYear = true): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!m) return day;
  const text = `${Number(m[3])} ${MONTHS_SHORT[Number(m[2]) - 1] ?? m[2]}`;
  return withYear ? `${text} ${m[1]}` : text;
}

/**
 * The window in words a person reads: "Last 30 days", "October", "All data
 * from 1 Sep 2025", "14 Sep to 13 Oct 2026". A custom range's own label is
 * two raw date keys with an arrow between them, which is not for the screen.
 */
export function windowWords(win: WindowState): string {
  if (win.label === ALL_WINDOW_LABEL) return `All data from ${dayWords(win.startDate)}`;
  if (!win.label.includes('→')) return win.label;
  if (win.startDate === win.endDate) return dayWords(win.endDate);
  const sameYear = win.startDate.slice(0, 4) === win.endDate.slice(0, 4);
  return `${dayWords(win.startDate, !sameYear)} to ${dayWords(win.endDate)}`;
}

/** Every day from start to end, both included. Never runs past today: a future day has no data. */
export function buildDayRange(startIso: string, endIso: string): string[] {
  const today = todayIso();
  const effectiveEnd = endIso > today ? today : endIso;
  const out: string[] = [];
  const end = parseIsoDay(effectiveEnd);
  const cur = parseIsoDay(startIso);
  while (cur <= end) {
    out.push(isoDay(cur));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

/**
 * How many days of the window have happened, both ends included. A window
 * that runs past today stops at today: the current month on the 13th is 13
 * days, not 31, so a per-day average over it is not diluted by days that
 * have not come yet. 0 when the whole window is in the future.
 */
export function windowDays(win: WindowState): number {
  const today = todayIso();
  const end = win.endDate > today ? today : win.endDate;
  const s = parseIsoDay(win.startDate).getTime();
  const e = parseIsoDay(end).getTime();
  return Math.max(0, Math.round((e - s) / 86_400_000) + 1);
}

/** Sum the days of a daily map that fall inside the window. */
export function sumDailyInWindow(map: Record<string, number> | undefined, win: WindowState): number {
  let s = 0;
  for (const [d, v] of Object.entries(map || {})) {
    if (d >= win.startDate && d <= win.endDate) s += v || 0;
  }
  return s;
}

/** A daily map laid out on every day of the window, for a chart. A day with no entry is 0. */
export function dailySeries(
  map: Record<string, number> | undefined,
  win: WindowState
): { days: string[]; values: number[] } {
  const days = buildDayRange(win.startDate, win.endDate);
  return { days, values: days.map((d) => map?.[d] || 0) };
}

/**
 * For a `{key: {day: value}}` map, the sum inside the window for each key.
 *
 * Every key in the map is in the result, 0 when it has nothing in the window.
 * A caller can so tell "nothing in the window" (0) from "this key is not in
 * the map" (no entry). The result used to drop a key whose sum was 0, and
 * every caller then read the missing key as "no daily data" and showed the
 * all-time number in its place.
 *
 * Use `windowSumsByKey` when the map itself can be absent.
 */
export function sumByKeyInWindow(
  map: Record<string, Record<string, number>> | undefined,
  win: WindowState
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [k, byDay] of Object.entries(map || {})) {
    let sum = 0;
    for (const [d, v] of Object.entries(byDay || {})) {
      if (d >= win.startDate && d <= win.endDate) sum += v || 0;
    }
    out[k] = sum;
  }
  return out;
}

/**
 * The same sums, or `undefined` when the snapshot does not carry the map at
 * all. `undefined` is the one case where an all-time figure is the correct
 * fallback, and the figure must then be tagged "all time".
 */
export function windowSumsByKey(
  map: Record<string, Record<string, number>> | undefined,
  win: WindowState
): Record<string, number> | undefined {
  return map === undefined ? undefined : sumByKeyInWindow(map, win);
}

// ─── model names ─────────────────────────────────────────────────────────

const MODEL_REGION_PREFIX = /^(global|us|au|eu|apac)\./;
// A name that says what it is with no maker in front of it.
const MODEL_FAMILY = /^(claude|gpt|o\d|nova|titan|llama|mistral|mixtral|command|qwen|gemma|glm)/;

/**
 * A model id as a person reads it. Bedrock puts the routing region in front
 * of the id (`global.`, `us.`, `au.`, `eu.`, `apac.`) and then the maker
 * (`anthropic.`). Neither is a different model: Claude Sonnet 4.6 reached
 * through three regions is one model, and the dashboard must show one row.
 * The maker stays when the rest of the name does not say what the model is
 * (`deepseek.v4-flash`).
 */
export function cleanModelName(id: string): string {
  let name = id.replace(MODEL_REGION_PREFIX, '');
  const m = /^([a-z0-9-]+)\.(.+)$/.exec(name);
  if (m && MODEL_FAMILY.test(m[2])) name = m[2];
  return name;
}

export interface ModelTotals {
  cost: number;
  convs: number;
  /** The raw model ids merged into this row. */
  ids: string[];
}

/**
 * Cost and conversations per model for the window, one row per model whatever
 * region it ran through.
 *
 * `windowed` is false when the snapshot carries no per-day model maps: the
 * rows are then the snapshot's lifetime totals and must be tagged "all time".
 * `convsExact` is true when the conversations are counted once each (by their
 * first day); otherwise a conversation counts once for each day it was active.
 */
export function modelsInWindow(
  chat: ChatBlock | undefined,
  win: WindowState
): { totals: Record<string, ModelTotals>; windowed: boolean; convsExact: boolean } {
  const totals: Record<string, ModelTotals> = {};
  const add = (id: string, cost: number, convs: number) => {
    const name = cleanModelName(id);
    const row = (totals[name] ||= { cost: 0, convs: 0, ids: [] });
    row.cost += cost;
    row.convs += convs;
    if (!row.ids.includes(id)) row.ids.push(id);
  };

  const costByModel = chat?.daily_cost_by_model;
  const startedByModel = chat?.daily_convs_started_by_model;
  const convsByModel = startedByModel ?? chat?.daily_convs_by_model;
  // An empty map is what an older summed view seeded, so it counts as absent.
  const hasDaily = Object.keys(costByModel || {}).length > 0 || Object.keys(convsByModel || {}).length > 0;
  if (!hasDaily) {
    for (const [id, v] of Object.entries(chat?.model_totals || {})) add(id, v?.cost || 0, v?.convs || 0);
    return { totals, windowed: false, convsExact: true };
  }

  const cost = sumByKeyInWindow(costByModel, win);
  const convs = sumByKeyInWindow(convsByModel, win);
  for (const id of new Set([...Object.keys(cost), ...Object.keys(convs)])) {
    add(id, cost[id] || 0, convs[id] || 0);
  }
  // A model with nothing in the window is not "in use" in the window.
  for (const name of Object.keys(totals)) {
    if (totals[name].cost === 0 && totals[name].convs === 0) delete totals[name];
  }
  return { totals, windowed: true, convsExact: startedByModel !== undefined };
}

/** Cost and conversations per model for the window. See `modelsInWindow`. */
export function modelTotalsInWindow(
  chat: ChatBlock | undefined,
  win: WindowState
): Record<string, { cost: number; convs: number }> {
  const out: Record<string, { cost: number; convs: number }> = {};
  for (const [name, v] of Object.entries(modelsInWindow(chat, win).totals)) {
    out[name] = { cost: v.cost, convs: v.convs };
  }
  return out;
}

// ─── conversations and runs ──────────────────────────────────────────────

/**
 * Does this conversation belong in the window?
 *
 * A conversation that started before the window and was still active inside
 * it is included: it cost money in the window. It is left out only when all
 * of its activity is outside the window.
 */
export function topConvInWindow(c: TopConversation, win: WindowState): boolean {
  const start = (c.started_at || '').slice(0, 10);
  const last = (c.last_request_at || '').slice(0, 10);
  const effectiveStart = start || last;
  const effectiveEnd = last || start;
  if (!effectiveStart && !effectiveEnd) return false;
  return effectiveStart <= win.endDate && effectiveEnd >= win.startDate;
}

export function topRunInWindow(r: TopScheduledRun, win: WindowState): boolean {
  const day = (r.completed_at || r.started_at || '').slice(0, 10);
  return day >= win.startDate && day <= win.endDate;
}

export function rollingMA(series: number[], window: number): (number | null)[] {
  const out: (number | null)[] = new Array(series.length).fill(null);
  let sum = 0;
  let count = 0;
  for (let i = 0; i < series.length; i++) {
    sum += series[i];
    count++;
    if (i >= window) {
      sum -= series[i - window];
      count = window;
    }
    out[i] = count ? sum / count : null;
  }
  return out;
}

/**
 * A unit cost over time as a trailing ratio: for each day, the sum of the
 * top series over the last `window` days divided by the sum of the bottom
 * series over the same days. Null where the bottom sums to 0.
 *
 * This is the average a person expects from "7-day average cost per
 * message": total cost over total messages for those 7 days. A mean of each
 * day's own ratio is a different number, pulled about by quiet days, and a
 * day with no messages has no ratio at all (it is not a ratio of 0).
 */
export function trailingRatio(top: number[], bottom: number[], window: number): Array<number | null> {
  const out: Array<number | null> = [];
  let sumTop = 0;
  let sumBottom = 0;
  for (let i = 0; i < top.length; i++) {
    sumTop += top[i] || 0;
    sumBottom += bottom[i] || 0;
    if (i >= window) {
      sumTop -= top[i - window] || 0;
      sumBottom -= bottom[i - window] || 0;
    }
    out.push(sumBottom > 0 ? sumTop / sumBottom : null);
  }
  return out;
}

export function topConvSpanSeconds(c: TopConversation): number | null {
  if (c.span_seconds != null) return c.span_seconds;
  if (!c.started_at || !c.last_request_at) return null;
  const a = Date.parse(c.started_at);
  const b = Date.parse(c.last_request_at);
  if (isNaN(a) || isNaN(b)) return null;
  return (b - a) / 1000;
}

/** A length of time in the two largest units: "45s", "12m", "3h 20m", "2d 4h". No length reads "n/a". */
export function fmtSpan(seconds: number | null): string {
  if (seconds == null) return 'n/a';
  if (seconds < 60) return Math.round(seconds) + 's';
  if (seconds < 3600) return Math.round(seconds / 60) + 'm';
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return h + 'h' + (m ? ' ' + m + 'm' : '');
  }
  const d = Math.floor(seconds / 86400);
  const h = Math.round((seconds % 86400) / 3600);
  return d + 'd' + (h ? ' ' + h + 'h' : '');
}

/** The months the view holds data for, newest first. Feeds the window control. */
export function discoverDataMonths(data: DashboardView): string[] {
  const set = new Set<string>();
  Object.keys(data.cost_explorer?.totals_by_day || {}).forEach((d) => set.add(d.slice(0, 7)));
  Object.keys(data.chat?.daily_cost || {}).forEach((d) => set.add(d.slice(0, 7)));
  Object.keys(data.chat?.daily_convs || {}).forEach((d) => set.add(d.slice(0, 7)));
  return Array.from(set).sort().reverse();
}

// One source of truth for chart colours. The PAL name is what the tabs import.
export { ND_CHART_PAL as PAL } from './theme';

export function isAggregate(data: DashboardView): data is AggregateView {
  return Boolean((data as AggregateView).is_aggregate);
}

// ─── what is loaded ──────────────────────────────────────────────────────

export const ALL_TIERS: FleetTier[] = ['users', 'agents', 'services', 'inventory', 'apps', 'conversations'];

/**
 * True when the object holds the named tiers' data. An object the portal did
 * not stamp (a snapshot read straight from S3, a test fixture) is complete.
 */
export function hasTier(data: { portal?: PortalMeta } | undefined, ...tiers: FleetTier[]): boolean {
  const meta = data?.portal;
  if (!meta) return true;
  return tiers.every((t) => meta.tiers.includes(t));
}

/** The tiers named that the object does not hold yet. */
export function tiersMissing(data: { portal?: PortalMeta } | undefined, ...tiers: FleetTier[]): FleetTier[] {
  const meta = data?.portal;
  if (!meta) return [];
  return tiers.filter((t) => !meta.tiers.includes(t));
}

/**
 * The tiers that must load before the named snapshot paths can be read
 * (`chat.by_user`, `cost_explorer.by_service_daily`). Empty when they are
 * all here.
 *
 * Which path lives in which tier is read from the index itself, never from a
 * list in this code: the rollup can move a path to another tier and every
 * selector follows. An object with no path list has nothing split off.
 */
export function tiersNeededFor(data: { portal?: PortalMeta } | undefined, ...paths: string[]): FleetTier[] {
  const meta = data?.portal;
  if (!meta?.tier_paths) return [];
  const out: FleetTier[] = [];
  for (const [tier, tierPaths] of Object.entries(meta.tier_paths) as Array<[FleetTier, string[] | undefined]>) {
    if (meta.tiers.includes(tier)) continue;
    if (paths.some((p) => tierPaths?.includes(p))) out.push(tier);
  }
  return out;
}

/** The same for a set of stacks: every tier any of them still needs. */
export function tiersNeededAcross(stacks: Array<{ portal?: PortalMeta }>, ...paths: string[]): FleetTier[] {
  const out = new Set<FleetTier>();
  // Every snapshot of one load shares one `portal` state, so the first of each state answers for the rest.
  const seen = new Set<PortalMeta | undefined>();
  for (const s of stacks) {
    if (seen.has(s.portal)) continue;
    seen.add(s.portal);
    for (const t of tiersNeededFor(s, ...paths)) out.add(t);
  }
  return Array.from(out);
}

/**
 * Merge one tier's fragment into a snapshot. A fragment holds its fields at
 * their own paths, so two maps merge key by key and anything else in the
 * fragment is taken as it is. Returns a new object; neither input changes.
 */
export function mergeTierFragment<T extends object>(base: T, fragment: object): T {
  const out = { ...base } as Record<string, unknown>;
  for (const [key, value] of Object.entries(fragment)) {
    const mine = out[key];
    const bothMaps =
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      mine !== null &&
      typeof mine === 'object' &&
      !Array.isArray(mine);
    out[key] = bothMaps ? mergeTierFragment(mine as object, value as object) : value;
  }
  return out as T;
}

// ─── freshness ───────────────────────────────────────────────────────────

/** Hours since an instant, or null when there is none. */
export function ageHours(iso: string | undefined, now: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, (now - t) / 3_600_000) : null;
}

/** A snapshot older than the nightly update allows for. */
export function isStaleAt(iso: string | undefined, now: number = Date.now()): boolean {
  const age = ageHours(iso, now);
  return age !== null && age > STALE_AFTER_HOURS;
}

/**
 * The day a snapshot's numbers run up to: the day it was gathered. "The last
 * 30 days" of a snapshot is measured back from this day, never from today's
 * date: a client whose nightly update stopped a month ago must read as "no
 * update for a month", not as "nobody used it for a month".
 */
export function asOfDay(s: { generated_at?: string } | undefined): string {
  const iso = s?.generated_at;
  return iso && Number.isFinite(Date.parse(iso)) ? iso.slice(0, 10) : todayIso();
}

/** The current New Zealand billing month, `YYYY-MM`. Credits are metered by New Zealand month. */
export function nzBillingMonth(now: number = Date.now()): string {
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date(now));
  const year = parts.find((p) => p.type === 'year')?.value ?? '';
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  return `${year}-${month}`;
}

// ─── which kind of stack ─────────────────────────────────────────────────
//
// `dev_instance: true` marks a developer or demo stack. `hq` carries the flag
// too, but it is Arcanum's own working Numa, used like a customer's. It is
// kept out of the dev group and out of the customer views.
const INTERNAL_CLIENT_NAMES = new Set(['hq']);

export function isRandDDevStack(clientName: string | undefined, devInstance: boolean | undefined): boolean {
  if (!devInstance) return false;
  if (clientName && INTERNAL_CLIENT_NAMES.has(clientName)) return false;
  return true;
}

/** Arcanum's own working Numa. Not a customer and not a dev stack. */
export function isInternalClient(clientName: string | undefined): boolean {
  if (!clientName) return false;
  return INTERNAL_CLIENT_NAMES.has(clientName);
}

/**
 * An Arcanum AWS account with no Numa stack in it: CI, image hosting, the
 * deployer, the organisation roots, the spare client accounts. It carries
 * cost and nothing else. Arcanum pays for it, so it belongs in Arcanum's
 * bill, and it must never be counted as a customer.
 */
export function isInfraAccount(s: ClientSnapshot): boolean {
  return Boolean(s.client_config?.infra_account);
}

/** The key prefix of an infrastructure account's snapshot (`_infra:arcanum-dev`). */
export const INFRA_CLIENT_PREFIX = '_infra:';

/** What to call an infrastructure account on screen: its AWS account name. */
export function infraDisplayName(
  s: ClientSnapshot | { client?: string; client_config?: { account_alias?: string } }
): string {
  const alias = s.client_config?.account_alias;
  if (alias) return alias;
  const c = s.client || '';
  return c.startsWith(INFRA_CLIENT_PREFIX) ? c.slice(INFRA_CLIENT_PREFIX.length) : c;
}

export type AccountOrg = 'nextgen' | 'arcanum' | 'standalone' | 'unknown';

/**
 * Which AWS organisation a stack's account is in. `unknown` is its own case:
 * a snapshot with no organisation on record is never taken for a customer's
 * own account (its bill would be cut as if it ran other systems) and never
 * taken for one of Arcanum's (its bill would be added to Arcanum's). The
 * record can be missing or wrong, and the dashboard only ever reads it.
 */
export function accountOrgOf(s: ClientSnapshot): AccountOrg {
  const org = s.client_config?.account_org;
  return org === 'nextgen' || org === 'arcanum' || org === 'standalone' ? org : 'unknown';
}

export type StackKind = 'customer' | 'dev' | 'internal' | 'infra';

export function stackKind(s: ClientSnapshot): StackKind {
  if (isInfraAccount(s)) return 'infra';
  if (isInternalClient(s.client)) return 'internal';
  if (isRandDDevStack(s.client, s.client_config?.dev_instance)) return 'dev';
  return 'customer';
}

/** What to call a stack or an account on screen. */
export function stackLabel(s: ClientSnapshot): string {
  return isInfraAccount(s) ? infraDisplayName(s) : s.client;
}

/**
 * A Nolia stack. The rollup writes `client_config.product`, and its
 * `product_of` holds the same rule. An older or failing snapshot has no
 * product, and then a name that starts with "nolia" marks a Nolia stack.
 */
export function isNoliaStack(s: ClientSnapshot): boolean {
  if (isInfraAccount(s)) return false;
  const product = s.client_config?.product;
  if (product !== undefined && product !== null) return product === 'nolia';
  return s.client.toLowerCase().startsWith('nolia');
}

/** `nolia` for a Nolia stack, `numa` for any other. */
export function productOf(s: ClientSnapshot): 'nolia' | 'numa' {
  return isNoliaStack(s) ? 'nolia' : 'numa';
}

// ─── the fleet-wide views ────────────────────────────────────────────────

export type FleetViewKind =
  | 'clients'
  | 'nextgen'
  | 'standalone'
  | 'nolia'
  | 'partner'
  | 'arcanum'
  | 'arcanum-infra'
  | 'fleet';

export interface FleetViewDef {
  id: string;
  /** The pool rule: see `viewMembers`. */
  kind: FleetViewKind;
  label: string;
  /** One sentence on exactly which stacks the view sums. It must stay true to `viewMembers`. */
  description: string;
  /** What the count beside the view counts. */
  unit: 'stacks' | 'accounts';
  group: 'customers' | 'partners' | 'arcanum';
  /** A `partner` view only: the partner's name in lower case (`partnerKey`). */
  partner?: string;
}

/**
 * The fixed fleet-wide views, in the order the sidebar lists them. The views
 * for each partner are built from the data (`partnerViews`) and go after
 * Nolia. The page, the sidebar and the exports all read `fleetViews`, so a
 * view has one name and one rule everywhere.
 *
 * The three Arcanum views count AWS accounts, not stacks: they hold accounts
 * with no stack at all, and a count of "stacks" there would be untrue.
 */
export const FLEET_VIEWS: FleetViewDef[] = [
  {
    id: '_CLIENTS',
    kind: 'clients',
    label: 'All customers',
    description: 'Every customer stack, Nolia stacks included. Dev and HQ are left out.',
    unit: 'stacks',
    group: 'customers',
  },
  {
    id: '_NEXTGEN',
    kind: 'nextgen',
    label: 'NextGen',
    description: 'Every stack in the NextGen organisation, dev and Nolia stacks included.',
    unit: 'stacks',
    group: 'customers',
  },
  {
    id: '_STANDALONE',
    kind: 'standalone',
    label: 'Standalone',
    description: 'Customers who pay their own AWS bill.',
    unit: 'stacks',
    group: 'customers',
  },
  {
    id: '_NOLIA',
    kind: 'nolia',
    label: 'Nolia',
    description: "Every Nolia stack, dev stacks included. Arcanum's bill leaves them out.",
    unit: 'stacks',
    group: 'partners',
  },
  {
    id: '_ARCANUM',
    kind: 'arcanum',
    label: "Arcanum's bill",
    description: 'What Arcanum pays AWS for: NextGen, internal and infrastructure accounts. Nolia stacks are left out.',
    unit: 'accounts',
    group: 'arcanum',
  },
  {
    id: '_ARCANUM_INFRA',
    kind: 'arcanum-infra',
    label: 'Infrastructure',
    description: 'Accounts with no Numa stack: CI, images, the deployer, the account pool.',
    unit: 'accounts',
    group: 'arcanum',
  },
  {
    id: '_FLEET',
    kind: 'fleet',
    label: 'Whole fleet',
    description: "Every stack and account: Arcanum's bill, the Nolia stacks and the standalone customers.",
    unit: 'accounts',
    group: 'arcanum',
  },
];

/** The start of a partner view's id: `_PARTNER:ohnyx`. */
export const PARTNER_VIEW_PREFIX = '_PARTNER:';

/** A partner's name as a key: trimmed, inner spaces collapsed, lower case. "Leading  Edge" and "leading edge" are one partner. */
export function partnerKey(name: string): string {
  return normalisePartner(name).toLowerCase();
}

/** The Nolia view holds Nolia's stacks, so a partner of that name gets no view of its own. */
const PARTNERS_WITHOUT_A_VIEW: ReadonlySet<string> = new Set(['nolia']);

function partnerViewDef(key: string, label: string): FleetViewDef {
  return {
    id: `${PARTNER_VIEW_PREFIX}${key}`,
    kind: 'partner',
    label,
    description: `Clients on a partner contract through ${label}.`,
    unit: 'stacks',
    group: 'partners',
    partner: key,
  };
}

/**
 * One view for each partner named on a partner contract, A to Z. The name
 * shown is the spelling on the first such stack by name. A partner whose only
 * stacks are Inactive still has its view: their spend counts.
 */
export function partnerViews(snapshots: ClientSnapshot[], contractOf: ContractOf = snapshotContract): FleetViewDef[] {
  const byKey = new Map<string, string>();
  const named = snapshots.filter((s) => !isInfraAccount(s)).sort((a, b) => a.client.localeCompare(b.client));
  for (const s of named) {
    const c = contractOf(s);
    if (c.contract !== 'partner' || !c.partner) continue;
    const key = partnerKey(c.partner);
    if (!key || PARTNERS_WITHOUT_A_VIEW.has(key) || byKey.has(key)) continue;
    byKey.set(key, c.partner);
  }
  return [...byKey].sort((a, b) => a[1].localeCompare(b[1])).map(([key, label]) => partnerViewDef(key, label));
}

/** Every fleet-wide view for this data, in sidebar order: the fixed views with each partner's after Nolia. */
export function fleetViews(snapshots: ClientSnapshot[], contractOf: ContractOf = snapshotContract): FleetViewDef[] {
  const partners = partnerViews(snapshots, contractOf);
  const at = FLEET_VIEWS.findIndex((v) => v.kind === 'nolia') + 1;
  return [...FLEET_VIEWS.slice(0, at), ...partners, ...FLEET_VIEWS.slice(at)];
}

/**
 * The view with this id, or undefined for a stack or an account. A partner id
 * that the data no longer names (its last client moved to another contract)
 * still reads as a view: it then holds no stacks, and the page says so.
 */
export function fleetViewById(id: string, views: readonly FleetViewDef[] = FLEET_VIEWS): FleetViewDef | undefined {
  const found = views.find((v) => v.id === id);
  if (found) return found;
  if (!id.startsWith(PARTNER_VIEW_PREFIX)) return undefined;
  const key = id.slice(PARTNER_VIEW_PREFIX.length);
  return key ? partnerViewDef(key, key) : undefined;
}

/** The live tags and contracts a view's members depend on. */
export interface ViewRules {
  /** Clients tagged Inactive. */
  inactive?: ReadonlySet<string>;
  /** Show Inactive clients in the lists and in the use and credit figures. Off by default. */
  showInactive?: boolean;
  /** Each stack's contract. Without it a stack's contract is read from its snapshot alone. */
  contractOf?: ContractOf;
}

/** No client is hidden. */
const NOBODY: ReadonlySet<string> = new Set();

/** The Inactive clients a view hides: all of them, unless the person asked to see them. */
export function hiddenBy(rules: ViewRules): ReadonlySet<string> {
  return rules.showInactive ? NOBODY : (rules.inactive ?? NOBODY);
}

export interface ViewMembers {
  /** The stacks and accounts whose use and credits the view counts, and that its lists show. */
  pool: ClientSnapshot[];
  /** Inactive stacks the view hides. Their spend still counts, because the money was spent. */
  costOnly: ClientSnapshot[];
}

/**
 * The stacks and accounts behind a fleet-wide view. The one rule for who is
 * in a view: the view's totals, every per-stack table, the sidebar counts and
 * the exports all call this.
 *
 *   - clients       customer stacks: not dev, not HQ, not an infrastructure account
 *   - nextgen       stacks in the NextGen organisation (dev and Nolia stacks included)
 *   - standalone    stacks in a customer's own AWS organisation
 *   - nolia         Nolia stacks (`isNoliaStack`), dev stacks included
 *   - partner       stacks on a partner contract through the view's partner
 *   - arcanum       NextGen stacks, Arcanum's own stacks (HQ, dev, demo) and
 *                   the infrastructure accounts, less the Nolia stacks. A
 *                   stack with no organisation on record is left out
 *   - arcanum-infra infrastructure accounts only
 *   - fleet         every row
 *
 * A client tagged Inactive is not a client in use. Every view moves it from
 * `pool` to `costOnly` unless `showInactive` is on: it is in no list and in no
 * use or credit figure, and its spend stays in the view's spend.
 */
export function viewMembers(
  snapshots: ClientSnapshot[],
  view: FleetViewDef | FleetViewKind | undefined,
  rules: ViewRules = {}
): ViewMembers {
  const members = membersByKind(snapshots, view, rules.contractOf ?? snapshotContract);
  const hidden = hiddenBy(rules);
  if (!hidden.size) return { pool: members, costOnly: [] };
  const pool: ClientSnapshot[] = [];
  const costOnly: ClientSnapshot[] = [];
  for (const s of members) (hidden.has(s.client) ? costOnly : pool).push(s);
  return { pool, costOnly };
}

/** The stacks and accounts whose use a view counts and lists. See `viewMembers`. */
export function poolForAggregate(
  snapshots: ClientSnapshot[],
  view: FleetViewDef | FleetViewKind | undefined,
  rules: ViewRules = {}
): ClientSnapshot[] {
  return viewMembers(snapshots, view, rules).pool;
}

function membersByKind(
  snapshots: ClientSnapshot[],
  view: FleetViewDef | FleetViewKind | undefined,
  contractOf: ContractOf
): ClientSnapshot[] {
  const kind = typeof view === 'string' ? view : view?.kind;
  switch (kind) {
    case 'clients':
      return snapshots.filter((s) => stackKind(s) === 'customer');
    case 'nextgen':
      return snapshots.filter((s) => !isInfraAccount(s) && accountOrgOf(s) === 'nextgen');
    case 'standalone':
      return snapshots.filter((s) => !isInfraAccount(s) && accountOrgOf(s) === 'standalone');
    case 'nolia':
      return snapshots.filter(isNoliaStack);
    case 'partner': {
      const key = typeof view === 'object' ? view.partner : undefined;
      if (!key) return [];
      return snapshots.filter((s) => {
        if (isInfraAccount(s)) return false;
        const c = contractOf(s);
        return c.contract === 'partner' && Boolean(c.partner) && partnerKey(c.partner!) === key;
      });
    }
    case 'arcanum':
      // Named organisations only. A stack with no organisation on record is
      // not assumed to be on Arcanum's bill: it is in "Whole fleet" alone.
      // Nolia stacks have a view of their own and are not on this bill.
      return snapshots.filter(
        (s) =>
          isInfraAccount(s) || ((accountOrgOf(s) === 'nextgen' || accountOrgOf(s) === 'arcanum') && !isNoliaStack(s))
      );
    case 'arcanum-infra':
      return snapshots.filter(isInfraAccount);
    case 'fleet':
    default:
      return snapshots;
  }
}

/** Stacks with no organisation on record. They are in "Whole fleet" and in no other Arcanum view. */
export function stacksWithUnknownOrg(snapshots: ClientSnapshot[]): ClientSnapshot[] {
  return snapshots.filter((s) => !isInfraAccount(s) && accountOrgOf(s) === 'unknown');
}

/**
 * What the data on screen changes about a view, in one sentence, or null. A
 * view's own description (`def.description`) is fixed and short. This is the
 * exception beside it: stacks with no organisation on record, which change
 * what the Arcanum views hold. The page shows it as a quiet line of its own.
 */
export function viewNote(def: FleetViewDef, snapshots: ClientSnapshot[]): string | null {
  const unknown = stacksWithUnknownOrg(snapshots).map((s) => s.client);
  if (!unknown.length || (def.kind !== 'arcanum' && def.kind !== 'fleet')) return null;
  const names =
    unknown.length <= 3 ? unknown.join(', ') : `${unknown.slice(0, 3).join(', ')} and ${unknown.length - 3} more`;
  const count = unknown.length === 1 ? '1 stack has' : `${unknown.length} stacks have`;
  return def.kind === 'arcanum'
    ? `${count} no organisation on record and ${unknown.length === 1 ? 'is' : 'are'} left out of this view: ${names}.`
    : `${count} no organisation on record and ${unknown.length === 1 ? 'is' : 'are'} counted in this view only: ${names}.`;
}

/** How many distinct AWS accounts a set of rows covers. A row with no account id counts as its own. */
export function uniqueAccountCount(snapshots: ClientSnapshot[]): number {
  const seen = new Set<string>();
  for (const s of snapshots) seen.add(s.client_config?.client_account_id || `unknown:${s.client}`);
  return seen.size;
}

/**
 * Drop an infrastructure row whose AWS account a client stack now uses. A
 * spare account is listed as infrastructure until a client is deployed into
 * it; the old infrastructure row can outlive that by weeks. Counted beside
 * the stack it would show the account twice in every list, and in a sum its
 * old cost data could be taken in place of the stack's fresh data.
 * Applied once, when the fleet data loads.
 */
export function dropClaimedInfraRows(snapshots: ClientSnapshot[]): ClientSnapshot[] {
  const stackAccounts = new Set<string>();
  // An account that was renamed leaves its old infrastructure row behind
  // too: of two infrastructure rows on one account, the freshest stays.
  const freshestInfra = new Map<string, ClientSnapshot>();
  for (const s of snapshots) {
    const acct = s.client_config?.client_account_id;
    if (!acct) continue;
    if (!isInfraAccount(s)) {
      stackAccounts.add(acct);
    } else {
      const held = freshestInfra.get(acct);
      if (!held || (s.generated_at || '') > (held.generated_at || '')) freshestInfra.set(acct, s);
    }
  }
  return snapshots.filter((s) => {
    const acct = s.client_config?.client_account_id;
    if (!isInfraAccount(s) || !acct) return true;
    return !stackAccounts.has(acct) && freshestInfra.get(acct) === s;
  });
}

// ─── model lines on the AWS bill ─────────────────────────────────────────

/**
 * The AWS bill lines for model use that Numa already prices from token
 * counts: "Claude Sonnet 4.6 (Amazon Bedrock Edition)", "OpenAI GPT-6 Luna
 * (Amazon Bedrock Edition)". The dashboard takes these out of the bill and
 * puts the token-priced chat cost in, because quota sharing bills one
 * client's model use to another client's account.
 *
 * "Amazon Bedrock" (embeddings) and "Amazon Bedrock AgentCore" (compute) are
 * not model lines. This is the one rule for a model line. The standalone
 * filter below uses it too, and the rollup's `cost_explorer.model_lines_daily`
 * is built with the same rule.
 */
const MODEL_LINE_PATTERN = /^(Claude|OpenAI) .* \(Amazon Bedrock Edition\)$/;

export function isModelLineService(service: string): boolean {
  return MODEL_LINE_PATTERN.test(service);
}

interface ModelLines {
  /** Model-line spend by day, USD. */
  daily: Record<string, number>;
  /** False when the snapshot holds nothing to work the lines out from. */
  known: boolean;
  /**
   * The first day `daily` covers when that is later than the bill's own first
   * day. Before it the model lines are unknown and stay inside the bill
   * total. Only a cut DynamoDB row has this.
   */
  from?: string;
}

const modelLinesCache = new WeakMap<CostExplorerBlock, ModelLines>();

function firstDay(map: Record<string, unknown> | undefined): string | undefined {
  let min: string | undefined;
  for (const d of Object.keys(map || {})) if (!min || d < min) min = d;
  return min;
}

/**
 * The model lines of one AWS bill by day.
 *
 * A newer snapshot carries them ready-made and full length
 * (`model_lines_daily`). An older one does not, and they are added up from
 * the per-service daily map, which the old DynamoDB rows cut to 92 days: past
 * that the lines cannot be taken out, and spend there is the bill plus chat
 * cost, counted twice. `from` marks where that starts so a figure can say so.
 */
export function modelLinesDaily(ce: CostExplorerBlock | undefined): ModelLines {
  if (!ce) return { daily: {}, known: true };
  const cached = modelLinesCache.get(ce);
  if (cached) return cached;

  let out: ModelLines;
  if (ce.model_lines_daily) {
    out = { daily: ce.model_lines_daily, known: true };
  } else if (ce.by_service_daily) {
    const daily: Record<string, number> = {};
    let firstServiceDay: string | undefined;
    for (const [svc, days] of Object.entries(ce.by_service_daily)) {
      const model = isModelLineService(svc);
      for (const [d, v] of Object.entries(days || {})) {
        if (!firstServiceDay || d < firstServiceDay) firstServiceDay = d;
        if (model) daily[d] = (daily[d] || 0) + (v || 0);
      }
    }
    const firstBillDay = firstDay(ce.totals_by_day);
    const cut = firstServiceDay && firstBillDay && firstServiceDay > firstBillDay;
    out = { daily, known: true, from: cut ? firstServiceDay : undefined };
  } else {
    // No bill at all is "known, and nothing". A bill with no breakdown is unknown.
    out = { daily: {}, known: !(ce.grand_total > 0) };
  }
  modelLinesCache.set(ce, out);
  return out;
}

// ─── standalone customers: Numa spend only ───────────────────────────────
//
// A standalone customer runs Numa in an AWS account that also runs their own
// systems: databases, search clusters, reporting. Their AWS bill is not
// Numa's cost. The filter keeps the services Numa deploys plus the model
// lines, and drops the rest.
//
// It applies only when `account_org` is `standalone`. A NextGen or Arcanum
// account runs nothing but Numa, so its whole bill is Numa cost.

export const NUMA_COST_SERVICES: ReadonlySet<string> = new Set([
  // Compute
  'Amazon Bedrock',
  'Amazon Bedrock AgentCore',
  'Amazon Q',
  'Amazon Q Business',
  'AWS Lambda',
  'AWS Step Functions',
  // Storage
  'Amazon DynamoDB',
  'Amazon Glacier',
  'Amazon SimpleDB',
  'Amazon OpenSearch Serverless', // the knowledge base store, not the provisioned 'Amazon OpenSearch Service'
  // Edge and API
  'Amazon API Gateway',
  'Amazon CloudFront',
  'Amazon Route 53',
  'Amazon Cognito',
  // Logs and security
  'AmazonCloudWatch',
  'CloudWatch Events',
  'AWS Key Management Service',
  'AWS Secrets Manager',
  'AWS Identity and Access Management',
  // Containers and messaging
  'Amazon EC2 Container Registry (ECR)',
  'Amazon Simple Notification Service',
  'Amazon Simple Queue Service',
  'Amazon Simple Workflow Service',
  'AWS Cost Explorer',
  // Numa app steps
  'Amazon Transcribe',
  'Amazon Comprehend',
  'Amazon Textract',
  'Amazon Simple Email Service',
  //
  // Left out on purpose for a standalone account:
  //   - ECS, Fargate, load balancers: only a Nolia stack uses them (`isNoliaStack`).
  //   - VPC, EC2 - Other, WAF: Numa's share is cents; a large bill is the customer's.
  //   - Glue: Numa does not use it; one standalone customer runs 1,197 USD of
  //     their own pipelines through it in 90 days.
  //   - S3: Numa's S3 spend per client is under 5 USD. A standalone S3 bill is
  //     the customer's own storage. Losing about 30 USD of real Numa S3 to
  //     drop about 700 USD of theirs is the better error.
  // When a Nolia stack runs in a standalone account, keep these three for it
  // by the same rule (`isNoliaStack`), or its Numa spend reads too low.
]);

/** A bill line that is Numa's cost in a standalone customer's account. */
export function isNumaAttributableService(service: string): boolean {
  return NUMA_COST_SERVICES.has(service) || isModelLineService(service);
}

const ceFilterCache = new WeakMap<CostExplorerBlock, { ce: CostExplorerBlock; droppedTotal: number }>();

const EMPTY_CE: CostExplorerBlock = { grand_total: 0, totals_by_service: {}, totals_by_day: {} };

/**
 * Take the customer's own systems out of a standalone account's bill.
 * Returns the bill unchanged for every other account. Safe to call twice on
 * the same bill.
 */
function applyCeFilter(
  ce: CostExplorerBlock | undefined,
  accountOrg: ClientConfigSummary['account_org']
): { ce: CostExplorerBlock; droppedTotal: number } {
  if (!ce) return { ce: EMPTY_CE, droppedTotal: 0 };
  if (accountOrg !== 'standalone') return { ce, droppedTotal: 0 };
  // Already filtered: the page hands tabs a filtered bill.
  if (ce.non_numa_filtered_total !== undefined) return { ce, droppedTotal: ce.non_numa_filtered_total };
  const cached = ceFilterCache.get(ce);
  if (cached) return cached;

  let droppedTotal = 0;
  let keptTotal = 0;
  const totals_by_service: Record<string, number> = {};
  for (const [svc, v] of Object.entries(ce.totals_by_service || {})) {
    if (isNumaAttributableService(svc)) {
      totals_by_service[svc] = v || 0;
      keptTotal += v || 0;
    } else {
      droppedTotal += v || 0;
    }
  }

  // Day by day where the per-service map reaches. The old DynamoDB rows cut
  // that map to 92 days while the daily totals run 14 months: for the days
  // before the map starts, keep the same share of each day's total that the
  // kept services hold over the whole period. Rebuilding only from the map
  // made every older day read as zero spend.
  let by_service_daily: Record<string, Record<string, number>> | undefined;
  const totals_by_day: Record<string, number> = {};
  let firstServiceDay: string | undefined;
  if (ce.by_service_daily) {
    by_service_daily = {};
    for (const [svc, days] of Object.entries(ce.by_service_daily)) {
      const keep = isNumaAttributableService(svc);
      if (keep) by_service_daily[svc] = days;
      for (const [d, v] of Object.entries(days || {})) {
        if (!firstServiceDay || d < firstServiceDay) firstServiceDay = d;
        if (keep) totals_by_day[d] = (totals_by_day[d] || 0) + (v || 0);
      }
    }
  }
  const billTotal = keptTotal + droppedTotal;
  const keptShare = billTotal > 0 ? keptTotal / billTotal : 0;
  const non_numa_filtered_daily: Record<string, number> = {};
  for (const [d, v] of Object.entries(ce.totals_by_day || {})) {
    if (!(firstServiceDay && d >= firstServiceDay)) totals_by_day[d] = (v || 0) * keptShare;
    const dropped = (v || 0) - (totals_by_day[d] || 0);
    if (dropped > 0) non_numa_filtered_daily[d] = dropped;
  }

  const out = {
    ce: {
      ...ce,
      grand_total: keptTotal,
      totals_by_service,
      totals_by_day,
      by_service_daily,
      non_numa_filtered_total: droppedTotal,
      non_numa_filtered_daily,
    },
    droppedTotal,
  };
  ceFilterCache.set(ce, out);
  return out;
}

/** One snapshot's AWS bill with a standalone customer's own systems taken out. */
export function ceForSnapshot(s: ClientSnapshot): { ce: CostExplorerBlock; droppedTotal: number } {
  return applyCeFilter(s.cost_explorer, s.client_config?.account_org);
}

/**
 * A snapshot ready to draw on its own: a standalone customer's bill is cut to
 * Numa's spend. Every single-stack figure (page, compare, exports) starts
 * from this, so none of them shows the customer's whole AWS bill as Numa cost.
 */
export function snapshotForDisplay(s: ClientSnapshot): ClientSnapshot {
  const { ce } = ceForSnapshot(s);
  return ce === s.cost_explorer ? s : { ...s, cost_explorer: ce };
}

// ─── one row per AWS account ─────────────────────────────────────────────

/**
 * The cost owner of each AWS account: the one snapshot a sum reads the
 * account's bill from.
 *
 * AWS bills by account, and several stacks can share one (the dev stacks all
 * sit in one account, and a few customers share one). Each of those stacks'
 * snapshots carries the same bill, so a sum takes one snapshot per account.
 *
 *   1. An active stack before an Inactive one (`inactive`). A hidden Inactive
 *      stack must never take an active client's bill out of sight with it.
 *   2. Then the FRESHEST: the first one in load order could be a stack whose
 *      update stopped months ago, and its old bill would then stand in for
 *      the account.
 */
export function costOwnerPerAccount(
  snapshots: ClientSnapshot[],
  inactive: ReadonlySet<string> = NOBODY
): ClientSnapshot[] {
  const byAccount = new Map<string, ClientSnapshot>();
  for (const s of snapshots) {
    // No account id: count it on its own. Better to over-report than to drop spend.
    const key = s.client_config?.client_account_id || `unknown:${s.client}`;
    const held = byAccount.get(key);
    if (!held || ownsBefore(s, held, inactive)) byAccount.set(key, s);
  }
  return Array.from(byAccount.values());
}

/** True when `a` is the better cost owner of the two: see `costOwnerPerAccount`. */
function ownsBefore(a: ClientSnapshot, b: ClientSnapshot, inactive: ReadonlySet<string>): boolean {
  const aActive = !inactive.has(a.client);
  const bActive = !inactive.has(b.client);
  if (aActive !== bActive) return aActive;
  return (a.generated_at || '') > (b.generated_at || '');
}

function addInto(target: Record<string, number>, src: Record<string, number> | undefined): void {
  for (const [k, v] of Object.entries(src || {})) target[k] = (target[k] || 0) + (v || 0);
}

function addKeyedInto(
  target: Record<string, Record<string, number>>,
  src: Record<string, Record<string, number>> | undefined
): void {
  for (const [key, byDay] of Object.entries(src || {})) addInto((target[key] ||= {}), byDay);
}

/**
 * The AWS bill of a set of stacks, each account counted once. A standalone
 * customer's bill is cut to Numa's spend before it is added.
 *
 * `costOnly` names the hidden Inactive stacks among `snapshots`. Their bills
 * are in the sum like any other. `inactiveSpend` carries their part of it
 * apart, with their model use, so spend can say how much of it they are.
 */
function dedupedCostExplorer(
  snapshots: ClientSnapshot[],
  servicesLoaded: boolean,
  costOnly: ReadonlySet<string>,
  inactive: ReadonlySet<string>
): {
  ce: CostExplorerBlock;
  modelLinesUnknown: string[];
  modelLinesFrom?: string;
  inactiveSpend?: InactiveSpendBlock;
} {
  let grand_total = 0;
  let non_numa_filtered_total = 0;
  const totals_by_service: Record<string, number> = {};
  const totals_by_day: Record<string, number> = {};
  const by_service_daily: Record<string, Record<string, number>> = {};
  const bedrock_usage_type_totals: Record<string, number> = {};
  const bedrock_usage_type_daily: Record<string, Record<string, number>> = {};
  const model_lines_daily: Record<string, number> = {};
  const non_numa_filtered_daily: Record<string, number> = {};
  const unbilled_chat_daily: Record<string, number> = {};
  const modelLinesUnknown: string[] = [];
  let modelLinesFrom: string | undefined;
  let anyUsageDaily = false;

  // Chat cost on the days after each stack's own bill ends: AWS has not
  // billed those days yet. Stack by stack, because each account's bill ends
  // on its own day. Model lines for those days are 0, and without this the
  // whole of that chat cost read as "billed to another account".
  for (const s of snapshots) {
    let billedTo: string | undefined;
    for (const d of Object.keys(s.cost_explorer?.totals_by_day || {})) if (!billedTo || d > billedTo) billedTo = d;
    if (!billedTo) continue;
    for (const [d, v] of Object.entries(s.chat?.daily_cost || {})) {
      if (d > billedTo && (v || 0) > 0) unbilled_chat_daily[d] = (unbilled_chat_daily[d] || 0) + v;
    }
  }

  // A hidden stack is also a poorer cost owner than a shown one, whatever its tag.
  const notFirst = costOnly.size ? new Set([...inactive, ...costOnly]) : inactive;
  const hiddenSpend: InactiveSpendBlock = { stacks: [], chat_daily: {}, bill_daily: {}, model_lines_daily: {} };
  for (const s of costOwnerPerAccount(snapshots, notFirst)) {
    const { ce, droppedTotal } = ceForSnapshot(s);
    non_numa_filtered_total += droppedTotal;
    addInto(non_numa_filtered_daily, ce.non_numa_filtered_daily);
    grand_total += ce.grand_total || 0;
    addInto(totals_by_service, ce.totals_by_service);
    addInto(totals_by_day, ce.totals_by_day);
    addInto(bedrock_usage_type_totals, ce.bedrock_usage_type_totals);
    if (servicesLoaded) {
      addKeyedInto(by_service_daily, ce.by_service_daily);
      if (ce.bedrock_usage_type_daily) {
        anyUsageDaily = true;
        addKeyedInto(bedrock_usage_type_daily, ce.bedrock_usage_type_daily);
      }
    }
    const lines = modelLinesDaily(ce);
    if (!lines.known) modelLinesUnknown.push(s.client);
    if (lines.from && (!modelLinesFrom || lines.from > modelLinesFrom)) modelLinesFrom = lines.from;
    addInto(model_lines_daily, lines.daily);
    // The bill of an account that only hidden stacks hold is theirs.
    if (costOnly.has(s.client)) {
      addInto(hiddenSpend.bill_daily, ce.totals_by_day);
      addInto(hiddenSpend.model_lines_daily, lines.daily);
    }
  }
  for (const s of snapshots) {
    if (!costOnly.has(s.client)) continue;
    hiddenSpend.stacks.push(s.client);
    addInto(hiddenSpend.chat_daily, s.chat?.daily_cost);
  }

  return {
    inactiveSpend: hiddenSpend.stacks.length ? hiddenSpend : undefined,
    ce: {
      grand_total,
      totals_by_service,
      totals_by_day,
      // Only when every account's breakdown is loaded. With the core data
      // alone, only standalone customers carry it, and a part of the fleet
      // must not pass for the whole.
      by_service_daily: servicesLoaded ? by_service_daily : undefined,
      bedrock_usage_type_totals,
      bedrock_usage_type_daily: anyUsageDaily ? bedrock_usage_type_daily : undefined,
      model_lines_daily,
      non_numa_filtered_total,
      non_numa_filtered_daily,
      unbilled_chat_daily,
    },
    modelLinesUnknown,
    modelLinesFrom,
  };
}

// ─── agents across stacks ────────────────────────────────────────────────

/**
 * The key of an agent in a summed view. Library and system agents carry one
 * id in every stack they are installed in (one library agent is in 14), so
 * an agent is told apart by its stack AND its id. Keyed by id alone, one
 * stack's daily cost overwrote another's.
 */
export function agentKey(client: string, agentId: string): string {
  return `${client}::${agentId}`;
}

/** The key to look an agent row up by in its view's per-agent daily maps. */
export function agentRowKey(a: PerAgentStats): string {
  return a.key ?? a.agent_id;
}

// ─── summing chat across stacks ──────────────────────────────────────────

const TOP_KEEP = 50;

function hasChatActivity(s: ClientSnapshot): boolean {
  return (s.chat?.totals?.convs || 0) > 0 || Object.keys(s.chat?.daily_cost || {}).length > 0;
}

/** Day-keyed chat series that every snapshot carries. */
const CHAT_DAILY_BASE = [
  'daily_cost',
  'daily_messages',
  'daily_convs',
  'daily_turns',
  'daily_tool_calls',
  'daily_active_users',
  'daily_scheduled_count',
  'daily_adhoc_count',
] as const;

/** Day-keyed chat series only a newer snapshot carries. A stack without one is left out of that figure. */
const CHAT_DAILY_NEW = [
  'daily_requests',
  'daily_errors',
  'daily_input_tokens',
  'daily_output_tokens',
  'daily_cache_read_tokens',
  'daily_cache_creation_tokens',
  'daily_span_seconds_sum',
  'daily_span_count',
] as const;

/** `{key: {day: n}}` series every snapshot carries, where the keys are the same in every stack. */
const CHAT_KEYED_BASE = [
  'daily_cost_by_category',
  'daily_messages_by_category',
  'daily_convs_by_category',
  'daily_turns_by_category',
  'daily_tool_calls_by_category',
  'daily_cost_by_model',
  'daily_convs_by_model',
] as const;

/** Per-person series. A person belongs to one stack, so the maps join with no clash. */
const CHAT_BY_USER = [
  'daily_cost_by_user',
  'daily_messages_by_user',
  'daily_convs_started_by_user',
  'daily_tool_calls_by_user',
  'daily_turns_by_user',
  'daily_span_seconds_sum_by_user',
  'daily_span_count_by_user',
] as const;

/** Per-agent series. Joined under `agentKey`, because an agent id repeats across stacks. */
const CHAT_BY_AGENT = [
  'daily_cost_by_agent',
  'daily_scheduled_cost_by_agent',
  'daily_adhoc_cost_by_agent',
  'daily_scheduled_count_by_agent',
  'daily_adhoc_count_by_agent',
] as const;

function rollupChat(snapshots: ClientSnapshot[], missing: Record<string, string[]>): ChatBlock {
  const withChat = snapshots.filter(hasChatActivity);
  const noteMissing = (field: string, client: string) => {
    (missing[`chat.${field}`] ||= []).push(client);
  };

  const totals = {
    cost: 0,
    convs: 0,
    turns: 0,
    requests: 0,
    user_messages: 0,
    errors: 0,
    users: 0,
    tool_calls: 0,
    input_tokens: 0,
    output_tokens: 0,
    cache_read_tokens: 0,
    cache_creation_tokens: 0,
  };
  const by_category: ByCategoryBlock = {
    agent_run: { convs: 0, cost: 0, user_messages: 0, turns: 0, tool_calls: 0, input_tokens: 0, output_tokens: 0 },
    agent_chat: { convs: 0, cost: 0, user_messages: 0, turns: 0, tool_calls: 0, input_tokens: 0, output_tokens: 0 },
    plain_chat: { convs: 0, cost: 0, user_messages: 0, turns: 0, tool_calls: 0, input_tokens: 0, output_tokens: 0 },
  };
  const scheduled_vs_adhoc = {
    scheduled: { count: 0, cost: 0, turns: 0, requests: 0, user_messages: 0 },
    adhoc: { count: 0, cost: 0, turns: 0, requests: 0, user_messages: 0 },
  };
  const tool_totals: Record<string, number> = {};
  const model_totals: Record<string, { cost: number; convs: number }> = {};
  const by_user: Record<string, PerUserStats> = {};
  const by_agent: PerAgentStats[] = [];
  const top_conversations: TopConversation[] = [];
  let allConversations = 0;
  let all_time_count = 0;
  let window_days = 0;

  const daily: Record<string, Record<string, number>> = {};
  const keyed: Record<string, Record<string, Record<string, number>>> = {};
  const dailyOf = (f: string) => (daily[f] ||= {});
  const keyedOf = (f: string) => (keyed[f] ||= {});
  for (const f of CHAT_DAILY_BASE) dailyOf(f);
  for (const f of CHAT_KEYED_BASE) keyedOf(f);

  for (const s of snapshots) {
    const chat = s.chat;
    if (!chat) continue;
    // The longest history in the view. The first stack's own value was used
    // before, and that could be 90, 420 or nothing.
    window_days = Math.max(window_days, chat.window_days || 0);
    all_time_count += chat.all_time_count || 0;

    const t = chat.totals;
    if (t) {
      for (const k of Object.keys(totals) as Array<keyof typeof totals>) totals[k] += t[k] || 0;
    }
    for (const k of ['agent_run', 'agent_chat', 'plain_chat'] as const) {
      const src = chat.by_category?.[k];
      if (!src) continue;
      for (const f of Object.keys(by_category[k]) as Array<keyof ByCategoryBlock['agent_run']>) {
        by_category[k][f] += src[f] || 0;
      }
    }
    for (const k of ['scheduled', 'adhoc'] as const) {
      const src = chat.scheduled_vs_adhoc?.[k];
      if (!src) continue;
      for (const f of Object.keys(scheduled_vs_adhoc[k]) as Array<keyof (typeof scheduled_vs_adhoc)['adhoc']>) {
        scheduled_vs_adhoc[k][f] += src[f] || 0;
      }
    }
    addInto(tool_totals, chat.tool_totals);
    for (const [k, v] of Object.entries(chat.model_totals || {})) {
      const row = (model_totals[k] ||= { cost: 0, convs: 0 });
      row.cost += v?.cost || 0;
      row.convs += v?.convs || 0;
    }

    for (const f of CHAT_DAILY_BASE) addInto(dailyOf(f), chat[f]);
    for (const f of CHAT_KEYED_BASE) addKeyedInto(keyedOf(f), chat[f]);

    const active = hasChatActivity(s);
    for (const f of CHAT_DAILY_NEW) {
      if (chat[f]) addInto(dailyOf(f), chat[f]);
      else if (active) noteMissing(f, s.client);
    }
    if (chat.daily_tool_calls_by_tool) addKeyedInto(keyedOf('daily_tool_calls_by_tool'), chat.daily_tool_calls_by_tool);
    else if (active) noteMissing('daily_tool_calls_by_tool', s.client);

    // Conversations by first day. A stack that does not carry the true count
    // yet adds its per-active-day count in its place, so the view still has a
    // number for the window; the stack is named in `missing` and the figure is
    // then shown as approximate.
    if (chat.daily_convs_started) {
      addInto(dailyOf('daily_convs_started'), chat.daily_convs_started);
      addKeyedInto(keyedOf('daily_convs_started_by_category'), chat.daily_convs_started_by_category);
      addKeyedInto(keyedOf('daily_convs_started_by_model'), chat.daily_convs_started_by_model);
    } else if (active) {
      noteMissing('daily_convs_started', s.client);
      addInto(dailyOf('daily_convs_started'), chat.daily_convs);
      addKeyedInto(keyedOf('daily_convs_started_by_category'), chat.daily_convs_by_category);
      addKeyedInto(keyedOf('daily_convs_started_by_model'), chat.daily_convs_by_model);
    }

    for (const [uid, row] of Object.entries(chat.by_user || {})) by_user[uid] = { ...row, client: s.client };

    // Who used Numa themselves. A stack that cannot tell yet (it has no
    // record of typed messages apart from schedule prompts) adds its record
    // of all messages in its place and is named in `missing`: the view then
    // has one map for everybody, and the figure says it is approximate.
    const typed = chat.daily_adhoc_messages_by_user ?? chat.daily_messages_by_user;
    if (typed) {
      const target = keyedOf('daily_adhoc_messages_by_user');
      for (const [uid, byDay] of Object.entries(typed)) target[uid] = byDay;
    }
    addInto(dailyOf('daily_adhoc_active_users'), chat.daily_adhoc_active_users ?? chat.daily_active_users);
    if (active && !chat.daily_adhoc_active_users) noteMissing('daily_adhoc_active_users', s.client);
    for (const f of CHAT_BY_USER) {
      const src = chat[f];
      if (!src) continue;
      const target = keyedOf(f);
      for (const [uid, byDay] of Object.entries(src)) target[uid] = byDay;
    }

    for (const row of chat.by_agent || []) {
      by_agent.push({ ...row, client: s.client, key: agentKey(s.client, row.agent_id) });
    }
    for (const f of CHAT_BY_AGENT) {
      const src = chat[f];
      if (!src) continue;
      const target = keyedOf(f);
      for (const [aid, byDay] of Object.entries(src)) target[agentKey(s.client, aid)] = byDay;
    }

    const top = chat.top_conversations || [];
    for (const c of top) top_conversations.push({ ...c, client: s.client });
    // What the stack holds in all, not what this copy of it lists: an old
    // DynamoDB row lists 15 of the 50 it says it kept.
    allConversations += (chat.top_conversations_kept ?? top.length) + (chat.top_conversations_dropped ?? 0);
  }

  by_agent.sort((a, b) => b.cost - a.cost);
  top_conversations.sort((a, b) => b.total_cost_usd - a.total_cost_usd);
  const top = top_conversations.slice(0, TOP_KEEP);

  // "Started" exists in the view only when one stack or more carries the
  // true count. With none, the selectors read `daily_convs` and say so.
  const anyStarted = withChat.some((s) => s.chat?.daily_convs_started);
  const anyTyped = withChat.some((s) => s.chat?.daily_adhoc_active_users);
  const anyTypedByUser = snapshots.some((s) => s.chat?.daily_adhoc_messages_by_user);
  const pick = (f: string) => (daily[f] && Object.keys(daily[f]).length ? daily[f] : undefined);
  const pickKeyed = (f: string) => (keyed[f] && Object.keys(keyed[f]).length ? keyed[f] : undefined);

  return {
    window_days: window_days || 90,
    all_time_count,
    top_conversations: top,
    top_conversations_kept: top.length,
    top_conversations_dropped: Math.max(0, allConversations - top.length),
    totals,
    by_user,
    by_agent,
    by_category,
    daily_cost: daily.daily_cost,
    daily_messages: daily.daily_messages,
    daily_convs: daily.daily_convs,
    daily_turns: daily.daily_turns,
    daily_tool_calls: daily.daily_tool_calls,
    daily_active_users: daily.daily_active_users,
    daily_scheduled_count: daily.daily_scheduled_count,
    daily_adhoc_count: daily.daily_adhoc_count,
    daily_cost_by_category: keyed.daily_cost_by_category,
    daily_messages_by_category: keyed.daily_messages_by_category,
    daily_convs_by_category: keyed.daily_convs_by_category,
    daily_turns_by_category: keyed.daily_turns_by_category,
    daily_tool_calls_by_category: keyed.daily_tool_calls_by_category,
    daily_cost_by_model: keyed.daily_cost_by_model,
    daily_convs_by_model: keyed.daily_convs_by_model,
    daily_cost_by_user: keyed.daily_cost_by_user,
    daily_messages_by_user: keyed.daily_messages_by_user,
    daily_convs_started_by_user: keyed.daily_convs_started_by_user,
    daily_tool_calls_by_user: keyed.daily_tool_calls_by_user,
    daily_turns_by_user: keyed.daily_turns_by_user,
    daily_span_seconds_sum_by_user: keyed.daily_span_seconds_sum_by_user,
    daily_span_count_by_user: keyed.daily_span_count_by_user,
    daily_adhoc_messages_by_user: anyTypedByUser ? keyed.daily_adhoc_messages_by_user : undefined,
    daily_adhoc_active_users: anyTyped ? daily.daily_adhoc_active_users : undefined,
    daily_cost_by_agent: keyed.daily_cost_by_agent,
    daily_scheduled_cost_by_agent: keyed.daily_scheduled_cost_by_agent,
    daily_adhoc_cost_by_agent: keyed.daily_adhoc_cost_by_agent,
    daily_scheduled_count_by_agent: keyed.daily_scheduled_count_by_agent,
    daily_adhoc_count_by_agent: keyed.daily_adhoc_count_by_agent,
    daily_tool_calls_by_tool: pickKeyed('daily_tool_calls_by_tool'),
    daily_requests: pick('daily_requests'),
    daily_errors: pick('daily_errors'),
    daily_input_tokens: pick('daily_input_tokens'),
    daily_output_tokens: pick('daily_output_tokens'),
    daily_cache_read_tokens: pick('daily_cache_read_tokens'),
    daily_cache_creation_tokens: pick('daily_cache_creation_tokens'),
    daily_span_seconds_sum: pick('daily_span_seconds_sum'),
    daily_span_count: pick('daily_span_count'),
    daily_convs_started: anyStarted ? daily.daily_convs_started : undefined,
    daily_convs_started_by_category: anyStarted ? keyed.daily_convs_started_by_category : undefined,
    daily_convs_started_by_model: anyStarted ? keyed.daily_convs_started_by_model : undefined,
    tool_totals,
    model_totals,
    scheduled_vs_adhoc,
  };
}

function rollupSchedules(snapshots: ClientSnapshot[]): SchedulesBlock {
  const out: SchedulesBlock = {
    count: 0,
    rows: [],
    by_status: {},
    by_derived_status: {},
    by_event_type: {},
    by_trigger: {},
    total_runs: 0,
    schedules_with_errors: 0,
    projected_runs_per_month_total: 0,
  };
  for (const s of snapshots) {
    const sb = s.schedules;
    if (!sb) continue;
    out.count += sb.count || 0;
    out.total_runs += sb.total_runs || 0;
    out.schedules_with_errors += sb.schedules_with_errors || 0;
    out.projected_runs_per_month_total += sb.projected_runs_per_month_total || 0;
    for (const r of sb.rows || []) out.rows.push({ ...r, client: s.client } as ScheduleRow);
    addInto(out.by_status, sb.by_status);
    addInto(out.by_derived_status, sb.by_derived_status);
    addInto(out.by_event_type!, sb.by_event_type);
    addInto(out.by_trigger!, sb.by_trigger);
  }
  return out;
}

function rollupScheduledRuns(snapshots: ClientSnapshot[], missing: Record<string, string[]>): ScheduledRunsBlock {
  const top_runs: TopScheduledRun[] = [];
  const totals = { count: 0, cost: 0, turns: 0 };
  const by_status: Record<string, number> = {};
  const by_agent: ScheduledRunsBlock['by_agent'] = {};
  const daily_runs: Record<string, number> = {};
  const daily_cost: Record<string, number> = {};
  const daily_failed_runs: Record<string, number> = {};
  let anyFailedDaily = false;
  let allRuns = 0;

  for (const s of snapshots) {
    const sr = s.scheduled_runs;
    if (!sr) continue;
    const top = sr.top_runs || [];
    top_runs.push(...top);
    allRuns += (sr.top_runs_kept ?? top.length) + (sr.top_runs_dropped ?? 0);
    totals.count += sr.totals?.count || 0;
    totals.cost += sr.totals?.cost || 0;
    totals.turns += sr.totals?.turns || 0;
    addInto(by_status, sr.by_status);
    for (const [aid, m] of Object.entries(sr.by_agent || {})) {
      const row = (by_agent[agentKey(s.client, aid)] ||= { ...m, runs: 0, cost: 0, turns: 0, errors: 0 });
      row.runs += m.runs || 0;
      row.cost += m.cost || 0;
      row.turns += m.turns || 0;
      row.errors += m.errors || 0;
    }
    addInto(daily_runs, sr.daily_runs);
    addInto(daily_cost, sr.daily_cost);
    if (sr.daily_failed_runs) {
      anyFailedDaily = true;
      addInto(daily_failed_runs, sr.daily_failed_runs);
    } else if ((sr.totals?.count || 0) > 0) {
      (missing['scheduled_runs.daily_failed_runs'] ||= []).push(s.client);
    }
  }

  top_runs.sort((a, b) => (b.cost || 0) - (a.cost || 0));
  const top = top_runs.slice(0, TOP_KEEP);

  return {
    top_runs: top,
    top_runs_kept: top.length,
    top_runs_dropped: Math.max(0, allRuns - top.length),
    totals,
    by_status,
    by_agent,
    daily_runs,
    daily_cost,
    daily_failed_runs: anyFailedDaily ? daily_failed_runs : undefined,
  };
}

function rollupImpact(snapshots: ClientSnapshot[], missing: Record<string, string[]>): ImpactBlock {
  const out: ImpactBlock = {
    total_time_saved_minutes: 0,
    per_agent_runs: {},
    total_schedule_runs: 0,
    total_agents: 0,
    total_schedules: 0,
    active_schedules: 0,
  };
  const daily: Record<string, number> = {};
  const dailyByAgent: Record<string, Record<string, number>> = {};
  let anyDaily = false;
  let anyDailyByAgent = false;
  for (const s of snapshots) {
    const im = s.impact;
    out.total_time_saved_minutes += im?.total_time_saved_minutes || 0;
    out.total_schedule_runs += s.schedules?.total_runs || 0;
    out.total_agents += (s.agents?.agents?.count || 0) + (s.agents?.user_agents?.count || 0);
    out.total_schedules += s.schedules?.count || 0;
    out.active_schedules += s.schedules?.by_derived_status?.active || 0;
    for (const [aid, n] of Object.entries(im?.per_agent_runs || {})) {
      out.per_agent_runs[agentKey(s.client, aid)] = n || 0;
    }
    if (im?.daily_time_saved_minutes) {
      anyDaily = true;
      addInto(daily, im.daily_time_saved_minutes);
    } else if ((im?.total_time_saved_minutes || 0) > 0) {
      (missing['impact.daily_time_saved_minutes'] ||= []).push(s.client);
    }
    if (im?.daily_time_saved_minutes_by_agent) {
      anyDailyByAgent = true;
      for (const [aid, byDay] of Object.entries(im.daily_time_saved_minutes_by_agent)) {
        dailyByAgent[agentKey(s.client, aid)] = byDay;
      }
    }
  }
  if (anyDaily) out.daily_time_saved_minutes = daily;
  if (anyDailyByAgent) out.daily_time_saved_minutes_by_agent = dailyByAgent;
  return out;
}

const APPS_DAILY_KEYS: UserAppsDailyKey[] = [
  'runs',
  'users',
  'apps_run',
  'build_runs',
  'automation_runs',
  'automation_failures',
  'credits',
  'cost_usd',
  'ai_runs',
  'publishes',
  'new_apps',
  'deleted_apps',
  'public_views',
  'submissions',
  'build_cost_usd',
];

/**
 * Apps and dashboards across the stacks that carry the block. A person and an
 * app each belong to one stack, so counts add with no clash. The daily
 * history is summed only over stacks that hold it (schema 2); the others are
 * named in `missing`.
 *
 * In this block a missing key means "not available", never zero. So a key is
 * in the sum only when one stack or more carries it: no key is ever created
 * here with a 0 that no stack reported.
 */
function rollupUserApps(snapshots: ClientSnapshot[], missing: Record<string, string[]>): UserAppsBlock | undefined {
  const withBlock = snapshots.filter((s) => s.user_apps?.available);
  if (!withBlock.length) return undefined;

  const apps: Record<string, number> = {};
  let byVisibility: Record<string, number> | undefined;
  let entrypointsByKind: Record<string, number> | undefined;
  const sources: Record<string, boolean> = {};
  let asOf: string | undefined;
  const totals: Record<'d7' | 'd30' | 'd90', Record<string, number>> = { d7: {}, d30: {}, d90: {} };
  const daily: Partial<Record<UserAppsDailyKey, Record<string, number>>> = {};
  const dailyByApp: Record<string, Record<string, Record<string, number>>> = {};
  const rows: UserAppRow[] = [];
  const users: Record<string, UserAppsPerson> = {};
  const data: Record<string, number> = {};
  let anyDaily = false;
  let anyRows = false;
  let anyUsers = false;
  let historyFrom: string | undefined;
  let historyCompleteFrom: string | undefined;
  let asOfBehind: string | undefined;
  let schema = 1;

  for (const s of withBlock) {
    const ua = s.user_apps!;
    for (const [k, v] of Object.entries(ua.apps || {})) {
      if (typeof v === 'number') apps[k] = (apps[k] || 0) + v;
    }
    if (ua.apps?.by_visibility) addInto((byVisibility ||= {}), ua.apps.by_visibility);
    if (ua.apps?.entrypoints_by_kind) addInto((entrypointsByKind ||= {}), ua.apps.entrypoints_by_kind);
    // A source counts as there when any stack in the view has it.
    for (const [k, v] of Object.entries(ua.sources || {})) sources[k] = sources[k] || Boolean(v);
    if (ua.as_of && (!asOf || ua.as_of > asOf)) asOf = ua.as_of;
    for (const [k, v] of Object.entries(ua.data || {})) {
      if (typeof v === 'number') data[k] = (data[k] || 0) + v;
    }
    if (ua.daily) {
      anyDaily = true;
      schema = Math.max(schema, ua.schema || 2);
      for (const key of APPS_DAILY_KEYS) {
        if (ua.daily[key]) addInto((daily[key] ||= {}), ua.daily[key]);
      }
      for (const span of ['d7', 'd30', 'd90'] as const)
        addInto(totals[span], ua.totals?.[span] as Record<string, number>);
      if (ua.history_from && (!historyFrom || ua.history_from < historyFrom)) historyFrom = ua.history_from;
      if (ua.history_from && (!historyCompleteFrom || ua.history_from > historyCompleteFrom)) {
        historyCompleteFrom = ua.history_from;
      }
      if (ua.as_of && (!asOfBehind || ua.as_of < asOfBehind)) asOfBehind = ua.as_of;
    } else if ((ua.apps?.total || 0) > 0) {
      (missing['user_apps.daily'] ||= []).push(s.client);
    }
    if (ua.rows) {
      anyRows = true;
      for (const r of ua.rows) rows.push({ ...r, client: s.client });
    }
    for (const [series, byApp] of Object.entries(ua.daily_by_app || {})) {
      const target = (dailyByApp[series] ||= {});
      for (const [appId, byDay] of Object.entries(byApp)) target[appId] = byDay;
    }
    if (ua.users) {
      anyUsers = true;
      for (const [sub, u] of Object.entries(ua.users)) users[sub] = { ...u, client: s.client };
    }
  }

  return {
    available: true,
    schema,
    sources: Object.keys(sources).length ? sources : undefined,
    as_of: asOf,
    as_of_behind: asOfBehind,
    history_from: historyFrom,
    history_complete_from: historyCompleteFrom,
    apps: {
      ...(apps as unknown as UserAppsInventory),
      by_visibility: byVisibility,
      entrypoints_by_kind: entrypointsByKind,
    },
    totals: anyDaily ? (totals as { d7: UserAppsTotals; d30: UserAppsTotals; d90: UserAppsTotals }) : undefined,
    daily: anyDaily ? daily : undefined,
    data: { available: true, ...data },
    rows: anyRows ? rows : undefined,
    daily_by_app: Object.keys(dailyByApp).length ? dailyByApp : undefined,
    users: anyUsers ? users : undefined,
  };
}

// ─── per-stack rows for the old tabs ─────────────────────────────────────

/**
 * The per-person daily record that says who used Numa themselves. A newer
 * snapshot keeps messages a person typed apart from prompts their schedules
 * sent; an older one has the two together, and a person whose schedule ran
 * overnight then reads as active. `exact` is false for the older kind, and
 * any figure built on it must say so.
 */
export function peopleActivityMap(chat: ChatBlock | undefined): {
  map: Record<string, Record<string, number>> | undefined;
  exact: boolean;
} {
  if (chat?.daily_adhoc_messages_by_user) return { map: chat.daily_adhoc_messages_by_user, exact: true };
  return { map: chat?.daily_messages_by_user, exact: false };
}

/** Distinct active people by day, by the same rule: people who typed a message when the snapshot can tell. */
export function dailyActivePeople(chat: ChatBlock | undefined): {
  map: Record<string, number> | undefined;
  exact: boolean;
} {
  if (chat?.daily_adhoc_active_users) return { map: chat.daily_adhoc_active_users, exact: true };
  return { map: chat?.daily_active_users, exact: false };
}

/** The words for a people figure counted from the older record. */
export const SCHEDULE_COUNTS_AS_ACTIVE =
  'This counts a person as active when a schedule they own ran, though they did not use Numa themselves. The next nightly update counts only people who used Numa themselves.';

/** Distinct people with a message inside the window, from one stack's per-person daily map. */
export function activePeopleInWindow(
  map: Record<string, Record<string, number>> | undefined,
  win: WindowState
): number {
  let n = 0;
  for (const byDay of Object.values(map || {})) {
    for (const [d, v] of Object.entries(byDay || {})) {
      if (d >= win.startDate && d <= win.endDate && (v || 0) > 0) {
        n++;
        break;
      }
    }
  }
  return n;
}

/**
 * Per-stack rows for the window. New tabs use `selectStackRows` in
 * selectors.ts, which says what period each column covers. This one stays for
 * the tabs written before it and for the exports.
 *
 *   - spend, chat cost, conversations, messages, scheduled runs, credits:
 *     summed inside the window.
 *   - active users: distinct people with a message in the window when the
 *     per-person data is loaded, otherwise the busiest single day.
 *   - time saved: the one rule. For the window from the daily record when
 *     the snapshot has one (`time_saved_scope: 'window'`); otherwise the
 *     lifetime figure, said to be so (`'all-time'`). Never a lifetime figure
 *     scaled down to look like the window's.
 *   - schedules, agents, knowledge bases, integrations, provisioned users:
 *     as they stand now. The window does not apply.
 */
export function buildByClientRowsWindowed(
  snapshots: ClientSnapshot[],
  win: WindowState,
  contractOf: ContractOf = snapshotContract
): ByClientRow[] {
  return snapshots
    .map((s) => {
      const cfg = s.client_config || {};
      const { ce } = ceForSnapshot(s);
      const sched = s.schedules || ({} as SchedulesBlock);
      const scheduled_runs_in_window = sumDailyInWindow(s.scheduled_runs?.daily_runs, win);

      let active_users: number;
      const people = peopleActivityMap(s.chat).map;
      if (people && hasTier(s, 'users')) {
        active_users = activePeopleInWindow(people, win);
      } else {
        active_users = 0;
        for (const [d, v] of Object.entries(dailyActivePeople(s.chat).map || {})) {
          if (d >= win.startDate && d <= win.endDate && v > active_users) active_users = v;
        }
      }

      const savedDaily = s.impact?.daily_time_saved_minutes;
      const time_saved_minutes = savedDaily
        ? sumDailyInWindow(savedDaily, win)
        : s.impact?.total_time_saved_minutes || 0;

      const cr = s.credits;
      return {
        client: s.client,
        dev_instance: Boolean(cfg.dev_instance),
        ce_total: sumDailyInWindow(ce.totals_by_day, win),
        chat_cost: sumDailyInWindow(s.chat?.daily_cost, win),
        convs: sumDailyInWindow(s.chat?.daily_convs_started ?? s.chat?.daily_convs, win),
        user_messages: sumDailyInWindow(s.chat?.daily_messages, win),
        active_users,
        provisioned_users: s.users?.provisioned_users || 0,
        schedules: sched.count || 0,
        active_schedules: sched.by_derived_status?.active || 0,
        scheduled_runs: scheduled_runs_in_window,
        agents: (s.agents?.agents?.count || 0) + (s.agents?.user_agents?.count || 0),
        kbs: s.integrations?.knowledge_bases?.count || 0,
        integrations: s.pipedream?.distinct_active_apps || 0,
        time_saved_minutes,
        time_saved_scope: savedDaily ? ('window' as const) : ('all-time' as const),
        bedrock_account: cfg.bedrock_account,
        region: cfg.region,
        client_account_id: cfg.client_account_id,
        credits_used_window: cr?.available ? sumDailyInWindow(cr.daily_credits, win) : null,
        credits_month_used: cr?.available ? (cr.current_month_used ?? 0) : null,
        credits_balance: cr?.available ? (cr.standing?.available_balance ?? 0) : null,
        ...contractFields(s, contractOf),
      };
    })
    .sort((a, b) => b.ce_total - a.ce_total);
}

/** The contract columns of a per-stack row. */
function contractFields(
  s: ClientSnapshot,
  contractOf: ContractOf
): Pick<ByClientRow, 'contract' | 'partner' | 'billing' | 'collectible' | 'product'> {
  if (isInfraAccount(s)) return { contract: null, partner: null, billing: null, collectible: false };
  const c = contractOf(s);
  return {
    contract: c.contract ?? null,
    partner: c.partner ?? null,
    billing: c.billing ?? null,
    collectible: c.collectible,
    product: productOf(s),
  };
}

/** Lifetime per-stack rows. Only the summed view's own `by_client` and `outliers` read these. */
function buildByClientRows(snapshots: ClientSnapshot[], contractOf: ContractOf): ByClientRow[] {
  return snapshots
    .map((s) => {
      const cfg = s.client_config || {};
      const { ce } = ceForSnapshot(s);
      const chat = s.chat?.totals || ({} as ChatBlock['totals']);
      const sched = s.schedules || ({} as SchedulesBlock);
      return {
        client: s.client,
        dev_instance: Boolean(cfg.dev_instance),
        ce_total: ce.grand_total || 0,
        chat_cost: chat.cost || 0,
        convs: chat.convs || 0,
        user_messages: chat.user_messages || 0,
        active_users: chat.users || 0,
        provisioned_users: s.users?.provisioned_users || 0,
        schedules: sched.count || 0,
        active_schedules: sched.by_derived_status?.active || 0,
        scheduled_runs: s.scheduled_runs?.totals?.count || 0,
        agents: (s.agents?.agents?.count || 0) + (s.agents?.user_agents?.count || 0),
        kbs: s.integrations?.knowledge_bases?.count || 0,
        integrations: s.pipedream?.distinct_active_apps || 0,
        time_saved_minutes: s.impact?.total_time_saved_minutes || 0,
        bedrock_account: cfg.bedrock_account,
        region: cfg.region,
        client_account_id: cfg.client_account_id,
        credits_used_window: null,
        credits_month_used: s.credits?.available ? (s.credits.current_month_used ?? 0) : null,
        credits_balance: s.credits?.available ? (s.credits.standing?.available_balance ?? 0) : null,
        ...contractFields(s, contractOf),
      };
    })
    .sort((a, b) => b.ce_total - a.ce_total);
}

/** Rows more than two standard deviations above the mean on spend or chat cost. */
export function detectOutliers(rows: Array<Pick<ByClientRow, 'client' | 'ce_total' | 'chat_cost'>>) {
  if (rows.length < 3) return [];
  const out: Array<{ client: string; metric: string; value: number; mean: number; z_score: number }> = [];
  for (const key of ['ce_total', 'chat_cost'] as const) {
    const vals = rows.map((r) => r[key]);
    const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
    const variance = vals.reduce((a, b) => a + (b - mean) ** 2, 0) / vals.length;
    const std = Math.sqrt(variance);
    if (std === 0) continue;
    for (const r of rows) {
      const z = (r[key] - mean) / std;
      if (z > 2) out.push({ client: r.client, metric: key, value: r[key], mean, z_score: z });
    }
  }
  return out;
}

// ─── a set of snapshots as one view ──────────────────────────────────────

/** Credits a stack used this month past its allocation. 0 when it stayed inside it. */
export function overAllocation(cr: { current_month_used?: number; current_month_allocation?: number }): number {
  return Math.max(0, (cr.current_month_used || 0) - (cr.current_month_allocation || 0));
}

export interface AggregateOptions {
  key: string;
  kind: string;
  /** The tier files merged into the snapshots. Leave out when the snapshots are complete. */
  tiers?: FleetTier[];
  /** Which paths live in which tier, from the index. Leave out when nothing is split off. */
  tierPaths?: PortalMeta['tier_paths'];
  /** The clock, for age and the billing month. Tests pass a fixed one. */
  now?: number;
  /**
   * Hidden Inactive stacks among the snapshots (`viewMembers(...).costOnly`).
   * They count toward spend and toward nothing else.
   */
  costOnly?: ReadonlySet<string>;
  /** Every client tagged Inactive, shown or hidden. A shared account's bill is read from an active stack first. */
  inactive?: ReadonlySet<string>;
  /** Each stack's contract, for the credits that are owed. Without it the snapshot's own contract is used. */
  contractOf?: ContractOf;
}

/**
 * Sum a set of client snapshots into one view.
 *
 * AWS spend is counted once per AWS account. Chat, people, schedules, credits
 * and apps are summed per stack.
 *
 * Stacks named in `costOnly` add their spend (their bill and their model use)
 * and nothing else: no people, no conversations, no credits, no rows.
 *
 * The view's time is the OLDEST snapshot in it, never "now": a sum is no
 * fresher than its oldest part, and a sum stamped with the present hid stacks
 * whose update stopped weeks ago. `stale_stacks` names them. Their history is
 * still summed.
 */
export function aggregateSnapshots(all: ClientSnapshot[], opts: AggregateOptions): AggregateView {
  const now = opts.now ?? Date.now();
  const tiers = opts.tiers ?? ALL_TIERS;
  const missing: Record<string, string[]> = {};
  const costOnly = opts.costOnly ?? NOBODY;
  const contractOf = opts.contractOf ?? snapshotContract;
  // The stacks whose use counts. Every block but the AWS bill is summed over these.
  const snapshots = costOnly.size ? all.filter((s) => !costOnly.has(s.client)) : all;

  const by_client = buildByClientRows(snapshots, contractOf);

  let oldest: string | undefined;
  let newest: string | undefined;
  let detailFrom: string | undefined;
  const stale_stacks: StaleStack[] = [];
  let users_active_30d = 0;
  let users_provisioned = 0;
  let workspaceAgents = 0;
  let personalAgents = 0;
  let kbs = 0;
  let dataConnectors = 0;
  let window_days = 0;
  const agentRows: AgentRow[] = [];
  const personalAgentRows: AgentRow[] = [];
  const kbRows: KnowledgeBaseRow[] = [];
  const sub_to_email: Record<string, string> = {};
  let total_active_connections = 0;
  let total_users_queried = 0;
  let total_users_with_data = 0;
  let total_users_with_errors = 0;
  let total_users_with_apps: number | undefined;
  let distinctAppsSum = 0;
  const apps_by_name: Record<
    string,
    { app_name: string; user_count: number; stack_count: number; user_subs: string[] }
  > = {};

  for (const s of snapshots) {
    const at = s.generated_at;
    if (at) {
      if (!oldest || at < oldest) oldest = at;
      if (!newest || at > newest) newest = at;
    }
    if (isStaleAt(at, now)) stale_stacks.push({ client: s.client, generated_at: at, age_hours: ageHours(at, now) });
    const from = s.portal?.detail_from;
    if (from && (!detailFrom || from > detailFrom)) detailFrom = from;
    window_days = Math.max(window_days, s.window_days || 0);

    users_active_30d += s.users?.modified_in_last_30d || 0;
    users_provisioned += s.users?.provisioned_users || 0;
    Object.assign(sub_to_email, s.users?.sub_to_email);
    // The two kinds of agent are counted apart. They used to be added into
    // "workspace" with "personal" left at 0.
    workspaceAgents += s.agents?.agents?.count || 0;
    personalAgents += s.agents?.user_agents?.count || 0;
    for (const r of s.agents?.agents?.rows || []) agentRows.push({ ...r, client: s.client });
    for (const r of s.agents?.user_agents?.rows || []) personalAgentRows.push({ ...r, client: s.client });
    kbs += s.integrations?.knowledge_bases?.count || 0;
    for (const r of s.integrations?.knowledge_bases?.rows || []) kbRows.push({ ...r, client: s.client });
    dataConnectors += s.integrations?.data_connectors?.count || 0;

    const pd = s.pipedream;
    if (pd) {
      total_active_connections += pd.active_connections || 0;
      total_users_queried += pd.users_queried || 0;
      total_users_with_data += pd.users_with_data || 0;
      total_users_with_errors += pd.users_with_errors || 0;
      // Absent on an older snapshot. It stays absent in the view until a stack carries it.
      if (pd.users_with_apps !== undefined) total_users_with_apps = (total_users_with_apps ?? 0) + pd.users_with_apps;
      else if ((pd.active_connections || 0) > 0) (missing['pipedream.users_with_apps'] ||= []).push(s.client);
      distinctAppsSum += pd.distinct_active_apps || 0;
      for (const a of pd.by_app_active || []) {
        const slot = (apps_by_name[a.app_name] ||= {
          app_name: a.app_name,
          user_count: 0,
          stack_count: 0,
          user_subs: [],
        });
        slot.user_count += a.user_count || 0;
        slot.stack_count += 1;
        if (Array.isArray(a.user_subs)) slot.user_subs.push(...a.user_subs);
      }
    }
  }
  stale_stacks.sort((a, b) => (b.age_hours ?? 0) - (a.age_hours ?? 0));
  const by_app_active = Object.values(apps_by_name).sort(
    (a, b) => b.user_count - a.user_count || a.app_name.localeCompare(b.app_name)
  );

  const chat = rollupChat(snapshots, missing);
  // The bill is summed over every stack, the hidden Inactive ones included: the money was spent.
  const {
    ce: cost_explorer,
    modelLinesUnknown,
    modelLinesFrom,
    inactiveSpend,
  } = dedupedCostExplorer(all, tiers.includes('services'), costOnly, opts.inactive ?? NOBODY);
  if (modelLinesUnknown.length) missing['cost_explorer.model_lines'] = modelLinesUnknown;

  // Credits. A stack's "used this month" is added only when its record is for
  // the current billing month: a stack whose update stopped in August still
  // says "current month: August", and its August usage must not be read as
  // this month's. Balances are added as last known.
  //
  // Owed and prepaid count only the stacks Arcanum can invoice for credits.
  // Any other stack can end a month below zero without owing anything, and
  // its use past the allocation is counted apart as "not billed".
  const billingMonth = nzBillingMonth(now);
  let cr_clients = 0;
  let cr_collectible = 0;
  let cr_month_used = 0;
  let cr_month_alloc = 0;
  let cr_prepaid = 0;
  let cr_owed = 0;
  let cr_not_billed_over = 0;
  const cr_daily: Record<string, number> = {};
  const cr_by_source: Record<string, number> = {};
  const cr_months: Record<string, number> = {};
  const cr_behind: string[] = [];
  for (const s of snapshots) {
    const cr = s.credits;
    if (!cr?.available) continue;
    cr_clients += 1;
    const collectible = contractOf(s).collectible;
    const current = !cr.current_month || cr.current_month === billingMonth;
    if (current) {
      cr_month_used += cr.current_month_used || 0;
      cr_month_alloc += cr.current_month_allocation || 0;
    } else {
      cr_behind.push(s.client);
    }
    if (collectible) {
      cr_collectible += 1;
      const bal = cr.standing?.available_balance || 0;
      if (bal >= 0) cr_prepaid += bal;
      else cr_owed += -bal;
    } else if (current) {
      cr_not_billed_over += overAllocation(cr);
    }
    addInto(cr_daily, cr.daily_credits);
    addInto(cr_by_source, cr.by_source);
    for (const [month, m] of Object.entries(cr.months || {})) {
      cr_months[month] = (cr_months[month] || 0) + (m?.credits_charged || 0);
    }
  }

  return {
    client: opts.key,
    is_aggregate: true,
    aggregate_kind: opts.kind,
    generated_at: oldest,
    oldest_generated_at: oldest,
    newest_generated_at: newest,
    stale_stacks,
    window_days: window_days || 90,
    stack_count: snapshots.length,
    unique_account_count: uniqueAccountCount(snapshots),
    cost_explorer,
    chat,
    schedules: rollupSchedules(snapshots),
    scheduled_runs: rollupScheduledRuns(snapshots, missing),
    agents: {
      agents: { count: workspaceAgents, rows: agentRows },
      user_agents: { count: personalAgents, rows: personalAgentRows },
    },
    integrations: {
      knowledge_bases: { count: kbs, rows: kbRows },
      data_connectors: { count: dataConnectors },
    },
    users: {
      modified_in_last_30d: users_active_30d,
      provisioned_users: users_provisioned,
      sub_to_email,
    },
    pipedream: {
      users_queried: total_users_queried,
      users_with_data: total_users_with_data,
      users_with_apps: total_users_with_apps,
      users_with_errors: total_users_with_errors,
      active_connections: total_active_connections,
      // Distinct across the view needs each stack's list of apps (inventory
      // tier). Without the lists this is the sum of each stack's own count,
      // which counts an app once per stack that uses it.
      distinct_active_apps: by_app_active.length || distinctAppsSum,
      by_app_active,
    },
    impact: rollupImpact(snapshots, missing),
    by_client,
    members: snapshots.map((s) => s.client),
    inactive_spend: inactiveSpend,
    credits_rollup: {
      clients_with_ledger: cr_clients,
      current_month: billingMonth,
      current_month_used: cr_month_used,
      current_month_allocation: cr_month_alloc,
      prepaid_total: cr_prepaid,
      owed_total: cr_owed,
      collectible_stacks: cr_collectible,
      not_billed_over: cr_not_billed_over,
      daily_credits: cr_daily,
      by_source: cr_by_source,
      months: cr_months,
      stacks_behind: cr_behind,
    },
    user_apps: rollupUserApps(snapshots, missing),
    outliers: detectOutliers(by_client),
    portal: {
      tiers,
      tier_paths: opts.tierPaths,
      detail_from: detailFrom,
      model_lines_from: modelLinesFrom,
      missing,
    },
  };
}

// ─── a selection as a view ───────────────────────────────────────────────

export interface ViewForSelection {
  /** The numbers to draw, or null when the selection has no data. */
  view: DashboardView | null;
  /**
   * The stacks and accounts behind it, as the lists show them: Inactive
   * stacks the view hides are not in it. One entry for a single stack or
   * account.
   */
  pool: ClientSnapshot[];
  /** Hidden Inactive stacks whose spend is in the view. Empty for one stack or account. */
  costOnly: ClientSnapshot[];
}

export interface ViewOptions extends ViewRules {
  tiers?: FleetTier[];
  tierPaths?: PortalMeta['tier_paths'];
  now?: number;
  /** The views to look the id up in. Defaults to `fleetViews` of the snapshots. */
  views?: readonly FleetViewDef[];
}

/**
 * The view for a sidebar selection: one of the fleet-wide views summed over
 * its members, or one stack with a standalone customer's bill cut to Numa's
 * spend. The page and the exports both build their views here, so an export
 * can never sum a different set of stacks from the screen.
 */
export function viewForSelection(id: string, snapshots: ClientSnapshot[], opts: ViewOptions = {}): ViewForSelection {
  const def = fleetViewById(id, opts.views ?? fleetViews(snapshots, opts.contractOf));
  if (def) {
    const { pool, costOnly } = viewMembers(snapshots, def, opts);
    if (!pool.length && !costOnly.length) return { view: null, pool, costOnly };
    const view = aggregateSnapshots([...pool, ...costOnly], {
      key: def.id,
      kind: def.kind,
      tiers: opts.tiers,
      tierPaths: opts.tierPaths,
      now: opts.now,
      costOnly: new Set(costOnly.map((s) => s.client)),
      inactive: opts.inactive,
      contractOf: opts.contractOf,
    });
    return { view, pool, costOnly };
  }
  const snap = snapshots.find((s) => s.client === id);
  if (!snap) return { view: null, pool: [], costOnly: [] };
  return { view: snapshotForDisplay(snap), pool: [snap], costOnly: [] };
}

// ─── filling in what a snapshot leaves out ───────────────────────────────

/**
 * Give a snapshot every block and list the tabs read, empty where the data is
 * not there. An infrastructure account carries cost and little else, and the
 * core data holds no per-person or per-agent lists until their tier loads: a
 * tab must be able to read `chat.by_user` or `schedules.rows` on any of them
 * and get an empty answer, never a crash. `portal.tiers` says what is loaded.
 */
export function withDefaults(s: ClientSnapshot): ClientSnapshot {
  const chat = (s.chat || {}) as Partial<ChatBlock>;
  const sr = (s.scheduled_runs || {}) as Partial<ScheduledRunsBlock>;
  const sched = (s.schedules || {}) as Partial<SchedulesBlock>;
  const ce = (s.cost_explorer || {}) as Partial<CostExplorerBlock>;
  return {
    ...s,
    window_days: s.window_days || chat.window_days || 0,
    cost_explorer: {
      ...ce,
      grand_total: ce.grand_total || 0,
      totals_by_service: ce.totals_by_service || {},
      totals_by_day: ce.totals_by_day || {},
    },
    chat: {
      ...chat,
      window_days: chat.window_days || s.window_days || 0,
      top_conversations: chat.top_conversations || [],
      totals: {
        cost: 0,
        convs: 0,
        turns: 0,
        requests: 0,
        user_messages: 0,
        errors: 0,
        users: 0,
        tool_calls: 0,
        input_tokens: 0,
        output_tokens: 0,
        cache_read_tokens: 0,
        cache_creation_tokens: 0,
        ...chat.totals,
      },
      by_user: chat.by_user || {},
      by_agent: chat.by_agent || [],
      daily_cost: chat.daily_cost || {},
      daily_messages: chat.daily_messages || {},
      daily_convs: chat.daily_convs || {},
      daily_turns: chat.daily_turns || {},
      daily_tool_calls: chat.daily_tool_calls || {},
      daily_active_users: chat.daily_active_users || {},
      daily_scheduled_count: chat.daily_scheduled_count || {},
      daily_adhoc_count: chat.daily_adhoc_count || {},
      tool_totals: chat.tool_totals || {},
      model_totals: chat.model_totals || {},
      scheduled_vs_adhoc: chat.scheduled_vs_adhoc || {
        scheduled: { count: 0, cost: 0, turns: 0, requests: 0, user_messages: 0 },
        adhoc: { count: 0, cost: 0, turns: 0, requests: 0, user_messages: 0 },
      },
    },
    schedules: {
      ...sched,
      count: sched.count || 0,
      rows: sched.rows || [],
      by_status: sched.by_status || {},
      by_derived_status: sched.by_derived_status || {},
      total_runs: sched.total_runs || 0,
      schedules_with_errors: sched.schedules_with_errors || 0,
      projected_runs_per_month_total: sched.projected_runs_per_month_total || 0,
    },
    scheduled_runs: {
      ...sr,
      top_runs: sr.top_runs || [],
      totals: { count: 0, cost: 0, turns: 0, ...sr.totals },
      by_status: sr.by_status || {},
      by_agent: sr.by_agent || {},
      daily_runs: sr.daily_runs || {},
      daily_cost: sr.daily_cost || {},
    },
    agents: {
      agents: { count: 0, ...s.agents?.agents, rows: s.agents?.agents?.rows || [] },
      user_agents: { count: 0, ...s.agents?.user_agents, rows: s.agents?.user_agents?.rows || [] },
    },
    integrations: {
      ...s.integrations,
      knowledge_bases: {
        count: 0,
        ...s.integrations?.knowledge_bases,
        rows: s.integrations?.knowledge_bases?.rows || [],
      },
    },
    users: s.users || {},
    pipedream: {
      ...s.pipedream,
      users_queried: s.pipedream?.users_queried || 0,
      users_with_data: s.pipedream?.users_with_data || 0,
      users_with_errors: s.pipedream?.users_with_errors || 0,
      active_connections: s.pipedream?.active_connections || 0,
      distinct_active_apps: s.pipedream?.distinct_active_apps || 0,
      by_app_active: s.pipedream?.by_app_active || [],
    },
    impact: {
      ...s.impact,
      total_time_saved_minutes: s.impact?.total_time_saved_minutes || 0,
      total_schedule_runs: s.impact?.total_schedule_runs || 0,
      total_agents: s.impact?.total_agents || 0,
      total_schedules: s.impact?.total_schedules || 0,
      active_schedules: s.impact?.active_schedules || 0,
      per_agent_runs: s.impact?.per_agent_runs || {},
    },
  };
}

// ─── shared AWS accounts ─────────────────────────────────────────────────

/** Names for the accounts that hold several stacks on purpose. */
export const KNOWN_ACCOUNTS: Record<string, string> = {
  '905418183804': 'Q-demo',
  '619071323471': 'HQ + Trial',
  '978450690680': 'Quota sharing',
};

export function friendlyAccountName(accountId: string | undefined | null): string {
  if (!accountId) return 'Unknown account';
  return KNOWN_ACCOUNTS[accountId] || `Account …${accountId.slice(-4)}`;
}
